// SentenceTranslation.tsx
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import styles from "./SentenceTranslation.module.css";

// --- API Imports ---
import { fetchVocabularyWords } from "../../API/Vocabulary/VocabularyAPI";
// fetchCourseOrderData now handled by LanguageControlsGrid
import { VocabularyData } from "../../API/types";
import { callOpenAI } from "../../API/API";
import {
  fetchExistingReconstructExercises,
  createReconstructExercise,
  updateReconstructExercise,
  deleteReconstructExercise,
} from "../../API/Lesson/SentenceTranslationAPI";

// --- GenAI Payload Imports ---
import {
  buildSentenceFromVocabListPayload,
  buildSpanishTranslationPayload,
  buildGermanTranslationPayload,
  buildItalianTranslationPayload,
  buildFrenchTranslationPayload,
  buildPortugueseTranslationPayload,
  buildEnglishTranslationPayload,
} from "../../GenAIPrompts/GenAIPrompts";

// --- Shared Components ---
import LanguageControlsGrid, { AVAILABLE_LANGUAGES, PROFICIENCY_LEVELS, DIFFICULTIES, DOMAINS } from "../../shared/LanguageControlsGrid/LanguageControlsGrid";

// --- Language Constants ---
const LANG_CODE_MAP: { [key: string]: string } = {
  English: "EN",
  Spanish: "ES",
  Italian: "IT",
  French: "FR",
  German: "DE",
  Portuguese: "PT",
};

// --- Helper Functions ---
function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const stripPunctuationFromSegment = (text: string): string =>
  text
    .replace(/[.,!?;:“”"(){}\[\]:\-–—]/g, "") // remove punctuation
    .trim();

// Helper function to format words ordered fields (Base and Target) with spaces
const formatWordsOrdered = (jsonString: string): string => {
  try {
    const parsed = JSON.parse(jsonString);
    if (Array.isArray(parsed)) {
      if (parsed.length > 0 && Array.isArray(parsed[0])) {
        // Array of arrays - join inner arrays with spaces, separate variations with " | "
        return parsed.map((arr: string[]) => arr.join(" ")).join(" | ");
      } else {
        // Simple array - join with spaces
        return parsed.join(" ");
      }
    }
    return jsonString;
  } catch {
    return jsonString;
  }
};

// Helper function to format distractors fields (Base and Target) with commas
const formatDistractors = (jsonString: string): string => {
  try {
    const parsed = JSON.parse(jsonString);
    if (Array.isArray(parsed)) {
      return parsed.join(", ");
    }
    return jsonString;
  } catch {
    return jsonString;
  }
};

// --- Interface Definitions ---
interface SentenceOutputData {
  sentence: string;
  correctWords: string[];
  distractorWords: string[];
}

interface MultiLanguageSentenceData {
  sentenceTarget: string;
  translations: {
    baseLangCode: string;
    sentenceSource: string;
    wordsOrderedBase: string;
    distractorsBase: string;
  }[];
  wordsOrderedTarget: string;
  distractorsTarget: string;
}

export interface ReconstructExerciseDBRecord {
  sectionId: string;
  exerciseId: string;
  wordId: string;
  lang_pair_word_id?: string;
  targetLanguage: string;
  baseLanguage: string;
  domain: string;
  proficiencyLevel: string;
  difficulty: string;
  sentenceSource: string;
  sentenceTarget: string;
  wordsOrderedBase: string;
  wordsOrderedTarget: string;
  distractorsBase: string;
  distractorsTarget: string;
  createdAt: string;
  selectedWordId: string;
}

interface GroupedSentence {
  word: string; // The word/phrase extracted from lang_pair_word_id
  targetSentence: string; // The primary target sentence (first variation)
  targetLanguage: string;
  variations: ReconstructExerciseDBRecord[]; // All base language variations
}
interface CourseOrderAPIResponseItem {
  Identifier: string;
  Vocabulary_List: string;
}
interface CourseSectionProcessed {
  identifier: string;
  vocabularyListRaw: string;
  sectionNameForAPI: string;
}
interface SelectedVocabItemEnhanced {
  wordData: VocabularyData;
  sourceSection: CourseSectionProcessed;
}

interface SentenceVariationEditable {
  id: string;
  text: string;
  correctWordsCsv: string;
  baseLangCode?: string; // Optional: which base language (EN, FR, DE, IT, PT) this variation is for
}
interface EditableSourceBundleForm {
  variations: SentenceVariationEditable[];
  distractorWordsCsv: string;
}
interface EditModalFormState extends Partial<ReconstructExerciseDBRecord> {
  _wordsOrderedTargetCsv?: string;
  _distractorsTargetCsv?: string;
  _wordsOrderedBaseCsv?: string;
  _distractorsBaseCsv?: string;
}

// ── NEW ──
// Each “SentenceContext” keeps its own selected words, generated AI data, form, and prepared exercises:
type SentenceContext = {
  id: string;                                      // unique ID
  name: string;                                    // e.g. "Sentence 1"
  selectedVocabularyWords: Map<string, SelectedVocabItemEnhanced>;
  generatedSourceSentenceData: SentenceOutputData | null;
  editableSourceBundleForm: EditableSourceBundleForm | null;
  preparedReconstructExercises: ReconstructExerciseDBRecord[];
  multiLanguageData?: MultiLanguageSentenceData; // Store multi-language AI generation data
};

const VocabularySentenceGenerator: React.FC = () => {
  // ── NEW ──
  // We store an array of sentence contexts and track which one is “active”
  const [sentenceContexts, setSentenceContexts] = useState<SentenceContext[]>([]);
  const [currentSentenceId, setCurrentSentenceId] = useState<string | null>(null);

  // ── NEW ──
  // Derive the “active” context (or null if none selected)
  const currentContext = useMemo(() => {
    if (!currentSentenceId) return null;
    return sentenceContexts.find((ctx) => ctx.id === currentSentenceId) || null;
  }, [sentenceContexts, currentSentenceId]);

  // The “shared” state for sourceLanguage, section, domain, etc. remains global:
  const [sourceLanguage, setSourceLanguage] = useState<string>("");
  const [section, setSection] = useState<string>("");
  const [domain, setDomain] = useState<string>(DOMAINS[0]);
  const [proficiencyLevel, setProficiencyLevel] = useState<string>(
    PROFICIENCY_LEVELS[2]
  );
  const [difficulty, setDifficulty] = useState<string>(DIFFICULTIES[1]);

  const [courseSections, setCourseSections] = useState<CourseSectionProcessed[]>([]);
  const [vocabularyList, setVocabularyList] = useState<VocabularyData[]>([]);

  const [existingReconstructExercises, setExistingReconstructExercises] =
    useState<ReconstructExerciseDBRecord[]>([]);
  const [loadingExistingExercises, setLoadingExistingExercises] =
    useState<boolean>(false);
  const [existingExercisesError, setExistingExercisesError] = useState<string | null>(null);
  const [baseLanguageFilter, setBaseLanguageFilter] = useState<string>("");
  // loadingSections now handled by LanguageControlsGrid
  const [loadingVocabulary, setLoadingVocabulary] = useState<boolean>(false);

  const [generatingSentence, setGeneratingSentence] = useState<boolean>(false);
  const [translatingSentences, setTranslatingSentences] = useState<boolean>(false);
  const [isSavingPrepared, setIsSavingPrepared] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);
  const [generalStatusMessage, setGeneralStatusMessage] = useState<string | null>(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingExercise, setEditingExercise] =
    useState<ReconstructExerciseDBRecord | null>(null);
  const [editedExerciseData, setEditedExerciseData] =
    useState<EditModalFormState>({});

  // --- AI Bulk Edit State ---
  const [showAIBulkEditModal, setShowAIBulkEditModal] = useState<boolean>(false);
  const [aiBulkEditPrompt, setAIBulkEditPrompt] = useState<string>("");
  const [isProcessingAIEdit, setIsProcessingAIEdit] = useState<boolean>(false);
  const [aiEditPreview, setAIEditPreview] = useState<EditModalFormState | null>(null);
  const [aiModalMode, setAIModalMode] = useState<'edit' | 'generate'>('edit'); // Track whether we're editing or generating

  // --- AI Sentence Generation State ---
  const [sentenceGenerationPrompt, setSentenceGenerationPrompt] = useState<string>("");

  const langCodeMap = useMemo(() => LANG_CODE_MAP, []);

  // Get unique base languages from existing exercises
  const uniqueBaseLanguages = useMemo(() => {
    const languages = new Set(existingReconstructExercises.map(ex => ex.baseLanguage));
    return Array.from(languages).sort();
  }, [existingReconstructExercises]);

  // Filter exercises based on selected base language
  // Helper function to extract word from lang_pair_word_id
  const extractWordFromLangPairId = (langPairWordId?: string): string => {
    if (!langPairWordId) return "";
    // Format: #Targ_ES#Base_EN#word or phrase
    const parts = langPairWordId.split('#');
    // Return the last part (the actual word)
    return parts[parts.length - 1] || "";
  };

  const filteredExercises = useMemo(() => {
    if (!baseLanguageFilter) return existingReconstructExercises;
    return existingReconstructExercises.filter(ex => ex.baseLanguage === baseLanguageFilter);
  }, [existingReconstructExercises, baseLanguageFilter]);

  // Group exercises by word (extracted from lang_pair_word_id)
  const groupedSentences = useMemo(() => {
    if (!filteredExercises || filteredExercises.length === 0) return [];

    const grouped = filteredExercises.reduce(
      (acc: { [key: string]: GroupedSentence }, exercise) => {
        // Extract word from lang_pair_word_id, or fall back to wordId
        const word = extractWordFromLangPairId(exercise.lang_pair_word_id) || exercise.wordId || "Unknown";

        // Parse the sentenceTarget to get the first variation for display
        let targetSentence = exercise.sentenceTarget;
        try {
          const parsed = JSON.parse(exercise.sentenceTarget);
          if (Array.isArray(parsed) && parsed.length > 0) {
            targetSentence = parsed[0]; // Use first sentence variation for display
          }
        } catch (e) {
          // If parsing fails, use the raw string
          targetSentence = exercise.sentenceTarget;
        }

        if (!acc[word]) {
          acc[word] = {
            word,
            targetSentence,
            targetLanguage: exercise.targetLanguage,
            variations: [],
          };
        }

        acc[word].variations.push(exercise);
        return acc;
      },
      {}
    );

    return Object.values(grouped).sort((a, b) => a.word.localeCompare(b.word));
  }, [filteredExercises]);

  // ── NEW ──
  // Called when the user clicks “+ Add Sentence”
  const handleAddSentence = () => {
    const newId = generateUUID();
    const newName = `Sentence ${sentenceContexts.length + 1}`;
    const newContext: SentenceContext = {
      id: newId,
      name: newName,
      selectedVocabularyWords: new Map(),
      generatedSourceSentenceData: null,
      editableSourceBundleForm: null,
      preparedReconstructExercises: [],
    };
    setSentenceContexts((prev) => [...prev, newContext]);
    setCurrentSentenceId(newId);
    // Clear any global status/error
    setGeneralStatusMessage(null);
    setError(null);
  };

  // ── NEW ──
  // Called when user clicks one of the sentence‐tabs
  const handleSelectSentence = (id: string) => {
    setCurrentSentenceId(id);
    setGeneralStatusMessage(null);
    setError(null);
  };

  // ── GLOBAL: Reset state when sourceLanguage changes ──
  useEffect(() => {
    setCourseSections([]);
    setSection("");
    setVocabularyList([]);
    setExistingReconstructExercises([]);
    setExistingExercisesError(null);
    setError(null);
    setGeneralStatusMessage(null);

    // Also: Clear out all sentence contexts, because changing language invalidates them
    setSentenceContexts([]);
    setCurrentSentenceId(null);
  }, [sourceLanguage]);

  // ── Handle sections loaded from LanguageControlsGrid ──
  const handleSectionsLoaded = useCallback((sections: any[]) => {
    const processedSections: CourseSectionProcessed[] = sections
      .map((item) => ({
        identifier: item.Identifier,
        vocabularyListRaw: item.Vocabulary_List,
        sectionNameForAPI: item.Vocabulary_List,
      }))
      .sort(
        (a, b) => parseInt(a.identifier, 10) - parseInt(b.identifier, 10)
      );
    setCourseSections(processedSections);
  }, []);

  // ── GLOBAL: Load vocabulary when section changes ──
  const loadVocabularyForSection = useCallback(async () => {
    setVocabularyList([]);
    if (!section || !sourceLanguage || courseSections.length === 0) return;
    const currentCourseSection = courseSections.find(
      (cs) => cs.sectionNameForAPI === section
    );
    if (!currentCourseSection) {
      setError(`Internal error: Section details not found for "${section}".`);
      return;
    }
    const sectionIdentifierForVocabAPI = currentCourseSection.vocabularyListRaw;
    setLoadingVocabulary(true);
    setError(null);
    try {
      const wordsData: VocabularyData[] = await fetchVocabularyWords(
        sectionIdentifierForVocabAPI
      );
      // ── DEDUPE ──
      const uniqueWords: VocabularyData[] = wordsData.filter(
        (item, idx, arr) =>
          arr.findIndex((w) => w.Targ_Word === item.Targ_Word) === idx
      );
      setVocabularyList(uniqueWords || []);
    } catch (err: any) {
      console.error(
        `Error fetching vocabulary for section "${section}" (using ID "${sectionIdentifierForVocabAPI}") (Status: ${
          err.status || "unknown"
        }):`,
        err
      );
      setError(
        `Failed to load vocabulary for "${
          currentCourseSection.sectionNameForAPI
        }". Status: ${err.status || err.message || "Unknown error"}`
      );
      setVocabularyList([]);
    } finally {
      setLoadingVocabulary(false);
    }
  }, [section, sourceLanguage, courseSections]);

  useEffect(() => {
    if (section && sourceLanguage) loadVocabularyForSection();
    else setVocabularyList([]);
    // Also: whenever section changes, clear out *all* sentence contexts,
    // because the vocabulary changed underneath them.
    setSentenceContexts([]);
    setCurrentSentenceId(null);
    setExistingReconstructExercises([]);
    setExistingExercisesError(null);
    setError(null);
    setGeneralStatusMessage(null);
  }, [section, sourceLanguage, loadVocabularyForSection]);

  // ── GLOBAL: Load existing exercises (per‐section, not per‐sentence) ──
  useEffect(() => {
    async function loadExistingExercises() {
      setExistingReconstructExercises([]);
      setExistingExercisesError(null);
      if (!section) return;
      setLoadingExistingExercises(true);
      try {
        const data = await fetchExistingReconstructExercises(section);
        setExistingReconstructExercises(data);
      } catch (err: any) {
        setExistingExercisesError(
          `Failed to load existing exercises for "${section}": ${
            err.message || "Unknown error"
          }`
        );
      } finally {
        setLoadingExistingExercises(false);
      }
    }
    loadExistingExercises();
  }, [section]);

  // ── NEW: Whenever the AI‐generated data for the *current* sentence arrives,
  // initialize that sentence's editable form state:
  // Now also handles multi-language data from AI generation
  useEffect(() => {
    if (!currentContext) return;
    const { generatedSourceSentenceData, multiLanguageData } = currentContext;

    if (generatedSourceSentenceData) {
      // Check if we have multi-language data (all 5 base languages)
      if (multiLanguageData && multiLanguageData.translations && multiLanguageData.translations.length === 5) {
        console.log("Creating form with multi-language data:", multiLanguageData);

        // Create variations for ALL 5 base languages
        const variations = multiLanguageData.translations.map((translation) => {
          const cleanedCorrectWords = translation.wordsOrderedBase
            .split(',')
            .map(w => w.trim())
            .map(stripPunctuationFromSegment)
            .filter((w) => w.length > 0);

          return {
            id: generateUUID(),
            text: translation.sentenceSource,
            correctWordsCsv: cleanedCorrectWords.join(", "),
            baseLangCode: translation.baseLangCode, // Store which language this is
          };
        });

        // Clean target distractors
        const cleanedDistractors = multiLanguageData.distractorsTarget
          .split(',')
          .map(w => w.trim())
          .map(stripPunctuationFromSegment)
          .filter((w) => w.length > 0);

        const newForm: EditableSourceBundleForm = {
          variations,
          distractorWordsCsv: cleanedDistractors.join(", "),
        };

        // Write it back into the current context:
        setSentenceContexts((prev) =>
          prev.map((ctx) =>
            ctx.id === currentContext.id
              ? {
                  ...ctx,
                  editableSourceBundleForm: newForm,
                }
              : ctx
          )
        );
      } else {
        // Fallback to single-language mode (legacy compatibility)
        const cleanedCorrectWords = generatedSourceSentenceData.correctWords
          .map(stripPunctuationFromSegment)
          .filter((w) => w.length > 0);

        const cleanedDistractors = generatedSourceSentenceData.distractorWords
          .map(stripPunctuationFromSegment)
          .filter((w) => w.length > 0);

        const newForm: EditableSourceBundleForm = {
          variations: [
            {
              id: generateUUID(),
              text: generatedSourceSentenceData.sentence,
              correctWordsCsv: cleanedCorrectWords.join(", "),
            },
          ],
          distractorWordsCsv: cleanedDistractors.join(", "),
        };

        // Write it back into the current context:
        setSentenceContexts((prev) =>
          prev.map((ctx) =>
            ctx.id === currentContext.id
              ? {
                  ...ctx,
                  editableSourceBundleForm: newForm,
                }
              : ctx
          )
        );
      }
    } else {
      // If generatedSourceSentenceData is null, clear out the form for that context:
      setSentenceContexts((prev) =>
        prev.map((ctx) =>
          ctx.id === currentContext.id
            ? { ...ctx, editableSourceBundleForm: null }
            : ctx
        )
      );
    }
  }, [currentContext?.generatedSourceSentenceData, currentContext?.multiLanguageData]);

  // ── HANDLERS FOR “GLOBAL” inputs: language/section/domain/etc. ──
  const handleSourceLanguageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => setSourceLanguage(e.target.value);

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSection(e.target.value);
    setGeneralStatusMessage(null);
    setError(null);
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setDomain(e.target.value);
  const handleProficiencyChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setProficiencyLevel(e.target.value);
  const handleDifficultyChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setDifficulty(e.target.value);

  const handleBaseLanguageFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setBaseLanguageFilter(e.target.value);

  // ── NEW: Handler to (de)select a vocabulary word in the current sentence’s context ──
  const handleVocabWordSelection = (vocabItem: VocabularyData) => {
    if (!currentContext) {
      setError("Please add/select a sentence first.");
      return;
    }

    setSentenceContexts((prev) =>
      prev.map((ctx) => {
        if (ctx.id !== currentContext.id) return ctx;

        const newMap = new Map(ctx.selectedVocabularyWords);
        const currentSectionInfo = courseSections.find(
          (cs) => cs.sectionNameForAPI === section
        );
        if (!currentSectionInfo) {
          setError(`Internal error: Section details not found for "${section}".`);
          return ctx;
        }

        if (newMap.has(vocabItem.Identifier)) {
          newMap.delete(vocabItem.Identifier);
        } else {
          newMap.set(vocabItem.Identifier, {
            wordData: vocabItem,
            sourceSection: currentSectionInfo,
          });
        }

        return {
          ...ctx,
          selectedVocabularyWords: newMap,
          generatedSourceSentenceData: null, // Clear out old AI data
          editableSourceBundleForm: null,
          preparedReconstructExercises: [],
        };
      })
    );
    setGeneralStatusMessage(null);
    setError(null);
  };

  // ── NEW: Generate source sentence for the *current* sentence's context ──
  const handleGenerateSourceSentence = async () => {
    if (!currentContext) {
      setError("Please add/select a sentence first.");
      return;
    }
    if (
      !sourceLanguage ||
      !section ||
      currentContext.selectedVocabularyWords.size === 0 ||
      !proficiencyLevel ||
      !difficulty ||
      !domain
    ) {
      setError(
        "Please select language, section, domain, proficiency, difficulty, and at least one vocabulary word (for the current sentence)."
      );
      return;
    }
    setGeneratingSentence(true);
    setError(null);
    setGeneralStatusMessage(null);

    // Clear out current sentence's AI data before generating
    setSentenceContexts((prev) =>
      prev.map((ctx) =>
        ctx.id === currentContext.id
          ? {
              ...ctx,
              generatedSourceSentenceData: null,
              editableSourceBundleForm: null,
              preparedReconstructExercises: [],
            }
          : ctx
      )
    );

    try {
      const targetLangCode = langCodeMap[sourceLanguage];
      if (!targetLangCode) throw new Error("Invalid source language code.");

      // Build vocabulary words from currentContext.selectedVocabularyWords
      const wordsForPayload = Array.from(
        currentContext.selectedVocabularyWords.values()
      ).map((item) => item.wordData.Targ_Word);

      let sectionContextDisplay = "";
      const selectedItemsWithSection = Array.from(
        currentContext.selectedVocabularyWords.values()
      );
      if (
        selectedItemsWithSection.length > 0 &&
        courseSections.length > 0
      ) {
        const sectionIdentifiers = selectedItemsWithSection
          .map((item) => parseInt(item.sourceSection.identifier, 10))
          .filter((id) => !isNaN(id));
        if (sectionIdentifiers.length > 0) {
          const maxNumericIdentifier = Math.max(...sectionIdentifiers);
          const contextRelevantSections = courseSections
            .filter((cs) => {
              const id = parseInt(cs.identifier, 10);
              return !isNaN(id) && id >= 1 && id <= maxNumericIdentifier;
            })
            .sort(
              (a, b) => parseInt(a.identifier, 10) - parseInt(b.identifier, 10)
            );
          sectionContextDisplay = contextRelevantSections
            .map((cs) => cs.vocabularyListRaw)
            .join(", ");
        }
      }
      if (!sectionContextDisplay && section) {
        const currentSectionData = courseSections.find(
          (cs) => cs.sectionNameForAPI === section
        );
        sectionContextDisplay = currentSectionData
          ? currentSectionData.vocabularyListRaw
          : section;
      }
      if (!sectionContextDisplay && selectedItemsWithSection.length > 0) {
        sectionContextDisplay = Array.from(
          new Set(
            selectedItemsWithSection.map(
              (item) => item.sourceSection.vocabularyListRaw
            )
          )
        ).join(", ");
      }

      // Build multi-language AI prompt
      const systemContent = `
You are an AI assistant specialized in generating sentence translation exercises for language learning applications.
Your task is to create a NEW sentence in the target language (${sourceLanguage} - ${targetLangCode}) with translations to ALL common base languages.

The sentence should incorporate AT LEAST ONE, and ideally several, of the following vocabulary words:
${wordsForPayload.map((word) => `- ${word}`).join("\n")}

IMPORTANT RULES:
1. Generate ONE new sentence in the target language (${targetLangCode})
2. The sentence should be appropriate for ${proficiencyLevel} level with difficulty ${difficulty}/3
3. The sentence should be relevant to the "${domain}" domain and the section context: "${sectionContextDisplay}"
4. Provide translations of this sentence to ALL base languages: EN (English), FR (French), DE (German), IT (Italian), PT (Portuguese)
5. For each language (both target and all base languages), create word segments as comma-separated phrases from the sentence
6. Generate relevant distractors (2-4 wrong words that could confuse learners) for each language
7. Ensure all sentences are natural and grammatically correct in their respective languages
8. Keep the same meaning across all language translations
9. For word segments, DO NOT separate closely bound grammatical units (like pronoun-verb, article-noun pairs)
10. Include punctuation marks as separate segments where appropriate

Examples of proper word segmentation:
- "Could you please help me with my homework?" → "Could you, please, help me, with my, homework, ?"
- "I would like a glass of water, please." → "I would like, a glass of, water, ,, please, ."
- "The children are playing in the park." → "The children, are playing, in, the park, ."

Output Format:
Respond ONLY with a valid JSON object matching this exact structure:
{
  "sentenceTarget": "sentence in target language (${targetLangCode})",
  "translations": [
    {
      "baseLangCode": "EN",
      "sentenceSource": "sentence in English",
      "wordsOrderedBase": "phrase1,phrase2,phrase3",
      "distractorsBase": "distractor1,distractor2,distractor3"
    },
    {
      "baseLangCode": "FR",
      "sentenceSource": "sentence in French",
      "wordsOrderedBase": "phrase1,phrase2,phrase3",
      "distractorsBase": "distractor1,distractor2,distractor3"
    },
    {
      "baseLangCode": "DE",
      "sentenceSource": "sentence in German",
      "wordsOrderedBase": "phrase1,phrase2,phrase3",
      "distractorsBase": "distractor1,distractor2,distractor3"
    },
    {
      "baseLangCode": "IT",
      "sentenceSource": "sentence in Italian",
      "wordsOrderedBase": "phrase1,phrase2,phrase3",
      "distractorsBase": "distractor1,distractor2,distractor3"
    },
    {
      "baseLangCode": "PT",
      "sentenceSource": "sentence in Portuguese",
      "wordsOrderedBase": "phrase1,phrase2,phrase3",
      "distractorsBase": "distractor1,distractor2,distractor3"
    }
  ],
  "wordsOrderedTarget": "phrase1,phrase2,phrase3",
  "distractorsTarget": "distractor1,distractor2,distractor3"
}`;

      const userContent = `
Target Language: ${sourceLanguage} (${targetLangCode})
Vocabulary Words to use: ${wordsForPayload.join(", ")}
Domain: ${domain}
Proficiency Level: ${proficiencyLevel}
Difficulty: ${difficulty}
Section Context: ${sectionContextDisplay}

${sentenceGenerationPrompt ? `Additional Instructions: ${sentenceGenerationPrompt}` : ''}

Please generate a sentence based on these parameters with translations to ALL base languages (EN, FR, DE, IT, PT) and return it in the exact JSON format specified.`;

      const payload = {
        messages: [
          {
            role: "system",
            content: systemContent.trim(),
          },
          {
            role: "user",
            content: userContent.trim(),
          },
        ],
        temperature: 0.7,
        max_tokens: 3000,
      };

      const result = await callOpenAI(payload);
      if (!result || !result.response)
        throw new Error("No response from AI for sentence generation.");

      // Parse AI response - handle both string and object responses
      let parsedResponse: MultiLanguageSentenceData;
      try {
        if (typeof result.response === 'object' && result.response !== null) {
          // Response is already parsed
          parsedResponse = result.response as MultiLanguageSentenceData;
        } else if (typeof result.response === 'string') {
          // Response is a string, need to parse it
          let jsonString = result.response;
          const startIndex = jsonString.indexOf("{");
          const endIndex = jsonString.lastIndexOf("}") + 1;

          if (startIndex !== -1 && endIndex > startIndex) {
            jsonString = jsonString.slice(startIndex, endIndex).trim();
            parsedResponse = JSON.parse(jsonString);
          } else {
            throw new Error("No valid JSON found in response");
          }
        } else {
          throw new Error(`Unexpected response type: ${typeof result.response}`);
        }
      } catch (parseError: any) {
        console.error("Failed to parse AI response:", parseError);
        console.error("Response was:", result.response);
        throw new Error(`Invalid response format from AI: ${parseError.message}`);
      }

      // Validate response structure
      if (!parsedResponse.sentenceTarget || !parsedResponse.translations || !Array.isArray(parsedResponse.translations)) {
        throw new Error("AI response missing required fields or translations array");
      }

      if (parsedResponse.translations.length !== 5) {
        throw new Error(`Expected 5 translations, got ${parsedResponse.translations.length}`);
      }

      console.log("Multi-language sentence data received:", parsedResponse);

      // Store the multi-language response in context
      // We'll use this to populate editableSourceBundleForm with all variations
      setSentenceContexts((prev) =>
        prev.map((ctx) =>
          ctx.id === currentContext.id
            ? {
                ...ctx,
                generatedSourceSentenceData: {
                  sentence: parsedResponse.sentenceTarget,
                  correctWords: parsedResponse.wordsOrderedTarget.split(',').map(w => w.trim()),
                  distractorWords: parsedResponse.distractorsTarget.split(',').map(w => w.trim())
                },
                multiLanguageData: parsedResponse // Store full multi-language data
              }
            : ctx
        )
      );

      setGeneralStatusMessage(`Successfully generated sentence with ${parsedResponse.translations.length} language variations!`);

    } catch (err: any) {
      console.error("Error generating source sentence:", err);
      setError(
        `Failed to generate source sentence: ${err.message || "Unknown error"}`
      );
    } finally {
      setGeneratingSentence(false);
    }
  };

  // Open AI modal for regenerating generated sentences
  const handleOpenAIRegenerationModal = () => {
    if (!currentContext || !currentContext.multiLanguageData) {
      setError("No generated sentences to regenerate.");
      return;
    }

    // Debug: Log vocabulary words available for regeneration
    const vocabWords = Array.from(currentContext.selectedVocabularyWords.values())
      .map((item) => item.wordData.Targ_Word);
    console.log("🔍 Opening AI Regeneration Modal");
    console.log("📚 Selected vocabulary words in currentContext:", vocabWords);
    console.log("📊 Vocabulary words count:", currentContext.selectedVocabularyWords.size);
    console.log("🎯 MultiLanguageData exists:", !!currentContext.multiLanguageData);
    console.log("🎯 Current sentence:", currentContext.multiLanguageData?.sentenceTarget);

    if (currentContext.selectedVocabularyWords.size === 0) {
      console.warn("⚠️ WARNING: No vocabulary words selected for this sentence context!");
      console.warn("The AI will generate a sentence without vocabulary word focus.");
    }

    console.log("✅ Setting AI Modal Mode to 'generate'");
    setAIModalMode('generate');
    setShowAIBulkEditModal(true);
    setAIBulkEditPrompt("");
    setAIEditPreview(null);
  };

  // ── Handlers for editing the "editableSourceBundleForm" inside the current sentence's context ──
  const handleVariationTextChange = (id: string, newText: string) => {
    if (!currentContext) return;
    setSentenceContexts((prev) =>
      prev.map((ctx) => {
        if (ctx.id !== currentContext.id || !ctx.editableSourceBundleForm)
          return ctx;
        return {
          ...ctx,
          editableSourceBundleForm: {
            ...ctx.editableSourceBundleForm!,
            variations: ctx.editableSourceBundleForm.variations.map((v) =>
              v.id === id ? { ...v, text: newText } : v
            ),
          },
        };
      })
    );
  };

  const handleVariationCorrectWordsCsvChange = (
    id: string,
    newCsv: string
  ) => {
    if (!currentContext) return;
    setSentenceContexts((prev) =>
      prev.map((ctx) => {
        if (ctx.id !== currentContext.id || !ctx.editableSourceBundleForm)
          return ctx;
        return {
          ...ctx,
          editableSourceBundleForm: {
            ...ctx.editableSourceBundleForm!,
            variations: ctx.editableSourceBundleForm.variations.map((v) =>
              v.id === id
                ? {
                    ...v,
                    correctWordsCsv: newCsv
                      .split(",")
                      .map(stripPunctuationFromSegment)
                      .join(", "),
                  }
                : v
            ),
          },
        };
      })
    );
  };

  const handleAddVariation = () => {
    if (!currentContext) return;
    setSentenceContexts((prev) =>
      prev.map((ctx) => {
        if (ctx.id !== currentContext.id || !ctx.editableSourceBundleForm)
          return ctx;

        const firstVariationText =
          ctx.editableSourceBundleForm.variations[0]?.text || "";
        const firstVariationCorrectWordsCsv =
          ctx.editableSourceBundleForm.variations[0]?.correctWordsCsv || "";

        return {
          ...ctx,
          editableSourceBundleForm: {
            ...ctx.editableSourceBundleForm!,
            variations: [
              ...ctx.editableSourceBundleForm!.variations,
              {
                id: generateUUID(),
                text: firstVariationText,
                correctWordsCsv: firstVariationCorrectWordsCsv,
              },
            ],
          },
        };
      })
    );
  };

  const handleRemoveVariation = (idToRemove: string) => {
    if (!currentContext) return;
    setSentenceContexts((prev) =>
      prev.map((ctx) => {
        if (ctx.id !== currentContext.id || !ctx.editableSourceBundleForm)
          return ctx;

        if (ctx.editableSourceBundleForm.variations.length <= 1) {
          setGeneralStatusMessage("Cannot remove the primary sentence variation.");
          return ctx;
        }

        return {
          ...ctx,
          editableSourceBundleForm: {
            ...ctx.editableSourceBundleForm!,
            variations: ctx.editableSourceBundleForm.variations.filter(
              (v) => v.id !== idToRemove
            ),
          },
        };
      })
    );
  };

  const handleDistractorWordsCsvChange = (newCsv: string) => {
    if (!currentContext) return;
    setSentenceContexts((prev) =>
      prev.map((ctx) =>
        ctx.id === currentContext.id && ctx.editableSourceBundleForm
          ? {
              ...ctx,
              editableSourceBundleForm: {
                ...ctx.editableSourceBundleForm!,
                distractorWordsCsv: newCsv,
              },
            }
          : ctx
      )
    );
  };

  // ── Prepare exercises directly from multi-language AI data (skips translation) ──
  const handlePrepareExercisesFromMultiLanguageData = async () => {
    if (!currentContext || !currentContext.multiLanguageData) {
      setError("No multi-language data available.");
      return;
    }

    const ctx = currentContext;
    const multiLangData = ctx.multiLanguageData!; // Non-null assertion since we checked above

    setTranslatingSentences(true);
    setError(null);
    setGeneralStatusMessage(null);

    try {
      // Determine finalSectionIdForDB
      let finalSectionIdForDB = section;
      if (ctx.selectedVocabularyWords.size > 0 && courseSections.length > 0) {
        let latestSectionProcessed: CourseSectionProcessed | null = null;
        let maxNumericId = -1;
        for (const selectedItem of ctx.selectedVocabularyWords.values()) {
          const numericId = parseInt(selectedItem.sourceSection.identifier, 10);
          if (!isNaN(numericId) && numericId > maxNumericId) {
            maxNumericId = numericId;
            latestSectionProcessed = selectedItem.sourceSection;
          }
        }
        if (latestSectionProcessed) {
          finalSectionIdForDB = latestSectionProcessed.sectionNameForAPI;
        }
      }

      const targetLangCode = langCodeMap[sourceLanguage];
      const wordIdValue = Array.from(ctx.selectedVocabularyWords.values())
        .map((item) => item.wordData.Identifier)
        .join(", ");

      // Parse target language data
      const wordsOrderedTargetArray = multiLangData.wordsOrderedTarget
        .split(',')
        .map((w: string) => w.trim())
        .map(stripPunctuationFromSegment)
        .filter((w: string) => w.length > 0);

      const distractorsTargetArray = multiLangData.distractorsTarget
        .split(',')
        .map((w: string) => w.trim())
        .map(stripPunctuationFromSegment)
        .filter((w: string) => w.length > 0);

      const exercisesForThisContext: ReconstructExerciseDBRecord[] = [];

      // Create one exercise for each base language translation
      for (const translation of multiLangData.translations) {
        const wordsOrderedBaseArray = translation.wordsOrderedBase
          .split(',')
          .map((w: string) => w.trim())
          .map(stripPunctuationFromSegment)
          .filter((w: string) => w.length > 0);

        const distractorsBaseArray = translation.distractorsBase
          .split(',')
          .map((w: string) => w.trim())
          .map(stripPunctuationFromSegment)
          .filter((w: string) => w.length > 0);

        const dbRecord: ReconstructExerciseDBRecord = {
          sectionId: finalSectionIdForDB,
          exerciseId: generateUUID(),
          wordId: wordIdValue,
          targetLanguage: targetLangCode,
          baseLanguage: translation.baseLangCode,
          domain: domain,
          proficiencyLevel: proficiencyLevel,
          difficulty: difficulty,
          sentenceTarget: JSON.stringify([multiLangData.sentenceTarget]),
          wordsOrderedTarget: JSON.stringify([wordsOrderedTargetArray]),
          distractorsTarget: JSON.stringify(distractorsTargetArray),
          sentenceSource: translation.sentenceSource,
          wordsOrderedBase: JSON.stringify(wordsOrderedBaseArray),
          distractorsBase: JSON.stringify(distractorsBaseArray),
          createdAt: new Date().toISOString(),
          selectedWordId: ""
        };

        exercisesForThisContext.push(dbRecord);
      }

      // Write back the prepared exercises into current sentence's context
      setSentenceContexts((prev) =>
        prev.map((ctxItem) =>
          ctxItem.id === currentContext.id
            ? { ...ctxItem, preparedReconstructExercises: exercisesForThisContext }
            : ctxItem
        )
      );

      setGeneralStatusMessage(
        `${exercisesForThisContext.length} exercises prepared for Sentence "${currentContext.name}" (all base languages).`
      );

    } catch (err: any) {
      console.error("Error preparing exercises from multi-language data:", err);
      setError(
        `Failed to prepare exercises: ${err.message || "Unknown error"}`
      );
    } finally {
      setTranslatingSentences(false);
    }
  };

  // ── NEW: When the user clicks "Translate & Prepare Exercises" for the current context ──
  const handleTranslateAndPrepareExercises = async () => {
    if (!currentContext) {
      setError("Please add/select a sentence first.");
      return;
    }
    const ctx = currentContext;

    // Check if we have multi-language data (from AI generation)
    // If so, skip translation and go directly to preparing exercises
    if (ctx.multiLanguageData && ctx.multiLanguageData.translations && ctx.multiLanguageData.translations.length === 5) {
      handlePrepareExercisesFromMultiLanguageData();
      return;
    }

    // Otherwise, continue with the normal translation workflow
    if (!ctx.editableSourceBundleForm || ctx.editableSourceBundleForm.variations.length === 0) {
      setError(
        "No source sentence data to translate. Please generate or ensure primary variation is valid."
      );
      setGeneralStatusMessage(
        "No source data available to translate. Please generate a sentence and ensure the primary variation is valid."
      );
      return;
    }
    const primaryVariation = ctx.editableSourceBundleForm.variations[0];
    if (!primaryVariation.text.trim()) {
      setGeneralStatusMessage(
        "Primary sentence variation cannot be empty before translating."
      );
      return;
    }
    const primaryCorrectWords = primaryVariation.correctWordsCsv
      .split(",")
      .map(stripPunctuationFromSegment)
      .filter((s) => s.length > 0);
    if (primaryCorrectWords.length === 0) {
      setGeneralStatusMessage(
        "Correct Words for the primary sentence variation cannot be empty before translating."
      );
      return;
    }
    for (let i = 0; i < ctx.editableSourceBundleForm.variations.length; i++) {
      const variation = ctx.editableSourceBundleForm.variations[i];
      if (!variation.text.trim()) {
        setGeneralStatusMessage(
          `Sentence text for variation ${i + 1} cannot be empty.`
        );
        return;
      }
      const correctWords = variation.correctWordsCsv
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (correctWords.length === 0) {
        setGeneralStatusMessage(
          `Correct Words for variation ${i + 1} cannot be empty.`
        );
        return;
      }
    }
    const commonDistractors = ctx.editableSourceBundleForm.distractorWordsCsv
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    if (!sourceLanguage) {
      setError("Source language not set.");
      return;
    }

    setTranslatingSentences(true);
    setError(null);
    setGeneralStatusMessage(null);

    // Determine finalSectionIdForDB (same logic, but shared across contexts)
    let finalSectionIdForDB = section;
    if (ctx.selectedVocabularyWords.size > 0 && courseSections.length > 0) {
      let latestSectionProcessed: CourseSectionProcessed | null = null;
      let maxNumericId = -1;
      for (const selectedItem of ctx.selectedVocabularyWords.values()) {
        const numericId = parseInt(selectedItem.sourceSection.identifier, 10);
        if (!isNaN(numericId) && numericId > maxNumericId) {
          maxNumericId = numericId;
          latestSectionProcessed = selectedItem.sourceSection;
        }
      }
      if (latestSectionProcessed) {
        finalSectionIdForDB = latestSectionProcessed.sectionNameForAPI;
      }
    }

    const sentenceDataForTranslationAPI: SentenceOutputData = {
      sentence: primaryVariation.text,
      correctWords: primaryCorrectWords,
      distractorWords: commonDistractors,
    };
    const originalSentenceJSON = JSON.stringify(sentenceDataForTranslationAPI);

    const initialSourceLangCode = langCodeMap[sourceLanguage];
    const initialSourceLangName = sourceLanguage;
    const targetTranslationLanguages = AVAILABLE_LANGUAGES.filter(
      (lang) => lang !== initialSourceLangName
    );
    const exercisesForThisContext: ReconstructExerciseDBRecord[] = [];
    let translationErrorOccurred = false;

    try {
      for (const targetTranslationLangName of targetTranslationLanguages) {
        const targetTranslationLangCode =
          langCodeMap[targetTranslationLangName];
        if (!targetTranslationLangCode) continue;

        let translationPayload;
        switch (targetTranslationLangName) {
          case "English":
            translationPayload = buildEnglishTranslationPayload(
              originalSentenceJSON,
              initialSourceLangName
            );
            break;
          case "Spanish":
            translationPayload = buildSpanishTranslationPayload(
              originalSentenceJSON,
              initialSourceLangName
            );
            break;
          case "German":
            translationPayload = buildGermanTranslationPayload(
              originalSentenceJSON,
              initialSourceLangName
            );
            break;
          case "French":
            translationPayload = buildFrenchTranslationPayload(
              originalSentenceJSON,
              initialSourceLangName
            );
            break;
          case "Italian":
            translationPayload = buildItalianTranslationPayload(
              originalSentenceJSON,
              initialSourceLangName
            );
            break;
          case "Portuguese":
            translationPayload = buildPortugueseTranslationPayload(
              originalSentenceJSON,
              initialSourceLangName
            );
            break;
          default:
            console.warn(
              `No translation payload for ${targetTranslationLangName}`
            );
            continue;
        }
        const translationResult = await callOpenAI(translationPayload);
        if (!translationResult || !translationResult.response) {
          console.warn(
            `No response from AI for ${targetTranslationLangName} translation. Skipping.`
          );
          translationErrorOccurred = true;
          continue;
        }
        let translatedData: SentenceOutputData;
        try {
          translatedData = JSON.parse(translationResult.response);
          if (
            !translatedData.sentence ||
            !translatedData.correctWords ||
            !translatedData.distractorWords
          ) {
            console.warn(
              `AI response for ${targetTranslationLangName} translation is not in the expected format. Response:`,
              translationResult.response
            );
            translationErrorOccurred = true;
            continue;
          }
        } catch (parseError) {
          console.warn(
            `Failed to parse AI response for ${targetTranslationLangName}. Error: ${parseError} Response:`,
            translationResult.response
          );
          translationErrorOccurred = true;
          continue;
        }

        // Build final arrays of “multiple variations”:
        const sentenceTargetArray = ctx.editableSourceBundleForm.variations
          .map((v) => v.text.trim())
          .filter((t) => t);
        const wordsOrderedTargetArrays = ctx.editableSourceBundleForm.variations.map(
          (v) =>
            v.correctWordsCsv
              .split(",")
              .map(stripPunctuationFromSegment)
              .filter((s) => s.length > 0)
        );
        const validVariationsIndices = sentenceTargetArray
          .map((text, index) =>
            text && wordsOrderedTargetArrays[index].length > 0 ? index : -1
          )
          .filter((index) => index !== -1);

        const finalSentenceTargetArray = validVariationsIndices.map(
          (i) => sentenceTargetArray[i]
        );
        const finalWordsOrderedTargetArrays = validVariationsIndices.map(
          (i) => wordsOrderedTargetArrays[i]
        );

        if (finalSentenceTargetArray.length === 0) {
          console.warn(
            `Skipping DB record for ${targetTranslationLangName} as no valid sentence variations remained.`
          );
          translationErrorOccurred = true;
          continue;
        }
        const distractorWordsArrayForDB = commonDistractors;
        const exerciseId = generateUUID();
        const wordIdValue = Array.from(ctx.selectedVocabularyWords.values())
          .map((item) => item.wordData.Identifier)
          .join(", ");

        const dbRecord: ReconstructExerciseDBRecord = {
          sectionId: finalSectionIdForDB,
          exerciseId: exerciseId,
          wordId: wordIdValue,
          targetLanguage: initialSourceLangCode,
          baseLanguage: targetTranslationLangCode,
          domain: domain,
          proficiencyLevel: proficiencyLevel,
          difficulty: difficulty,
          sentenceTarget: JSON.stringify(finalSentenceTargetArray),
          wordsOrderedTarget: JSON.stringify(finalWordsOrderedTargetArrays),
          distractorsTarget: JSON.stringify(distractorWordsArrayForDB),
          sentenceSource: translatedData.sentence,
          wordsOrderedBase: JSON.stringify(translatedData.correctWords),
          distractorsBase: JSON.stringify(translatedData.distractorWords),
          createdAt: new Date().toISOString(),
          selectedWordId: ""
        };
        exercisesForThisContext.push(dbRecord);
      }

      // Write back the prepared exercises into current sentence’s context:
      setSentenceContexts((prev) =>
        prev.map((ctxItem) =>
          ctxItem.id === currentContext.id
            ? { ...ctxItem, preparedReconstructExercises: exercisesForThisContext }
            : ctxItem
        )
      );

      if (translationErrorOccurred) {
        setGeneralStatusMessage(
          "One or more translations failed or had issues. Check console."
        );
      } else if (exercisesForThisContext.length > 0) {
        setGeneralStatusMessage(
          `${exercisesForThisContext.length} exercise pairs prepared for Sentence "${currentContext.name}".`
        );
      } else {
        setGeneralStatusMessage(
          "No exercises were prepared. This might be due to issues with sentence variations after editing or other translation problems."
        );
      }
    } catch (err: any) {
      console.error("Error during the translation loop:", err);
      setError(
        `Translation process failed: ${
          err.message || "Unknown error during translation loop."
        }`
      );
    } finally {
      setTranslatingSentences(false);
    }
  };

  // ── NEW: Save all prepared exercises for the current sentence ──
  const handleSaveAllPreparedExercises = async () => {
    if (!currentContext) {
      setError("Please add/select a sentence first.");
      return;
    }
    const ctx = currentContext;
    if (!ctx.preparedReconstructExercises || ctx.preparedReconstructExercises.length === 0) {
      setGeneralStatusMessage("No prepared exercises to save for this sentence.");
      return;
    }
    setIsSavingPrepared(true);
    setGeneralStatusMessage(
      `Saving ${ctx.preparedReconstructExercises.length} exercises for ${ctx.name}...`
    );

    let successCount = 0;
    let errorCount = 0;
    const errorMessages: string[] = [];
    const successfullySavedForUI: ReconstructExerciseDBRecord[] = [];

    const savePromises = ctx.preparedReconstructExercises.map((exercise) =>
      createReconstructExercise(exercise)
    );
    const results = await Promise.allSettled(savePromises);
    results.forEach((result, index) => {
      const originalExercise = ctx.preparedReconstructExercises[index];
      if (result.status === "fulfilled") {
        successCount++;
        successfullySavedForUI.push(originalExercise);
      } else {
        errorCount++;
        const errorMessage =
          result.reason instanceof Error
            ? result.reason.message
            : String(result.reason);
        errorMessages.push(
          `Exercise ID ${originalExercise.exerciseId} (Section ${originalExercise.sectionId}): ${errorMessage}`
        );
        console.error(
          `Error saving exercise ${originalExercise.exerciseId}:`,
          result.reason
        );
      }
    });

    let summaryMessage = `${successCount} exercise(s) saved successfully for ${ctx.name}.`;
    if (errorCount > 0) {
      summaryMessage += `\n${errorCount} exercise(s) failed to save. Errors:\n- ${errorMessages.join(
        "\n- "
      )}`;
    }

    // If any were saved, we should add them to the global “existingReconstructExercises”
    if (successfullySavedForUI.length > 0) {
      setExistingReconstructExercises((prev) =>
        [...successfullySavedForUI, ...prev]
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          .filter(
            (exercise, index, self) =>
              index ===
              self.findIndex((e) => e.exerciseId === exercise.exerciseId)
          )
      );
      summaryMessage += `\nAdded ${successfullySavedForUI.length} to existing exercises.`;
    }

    setGeneralStatusMessage(summaryMessage);

    // Clear out this sentence’s prepared list after saving:
    setSentenceContexts((prev) =>
      prev.map((ctxItem) =>
        ctxItem.id === ctx.id
          ? { ...ctxItem, preparedReconstructExercises: [] }
          : ctxItem
      )
    );

    setIsSavingPrepared(false);
  };

  // ── Handler to delete an existing exercise from “existingReconstructExercises” ──
  const handleDeleteExistingExercise = async (
    exerciseToDelete: ReconstructExerciseDBRecord
  ) => {
    if (
      !window.confirm(
        `Are you sure you want to delete exercise "${exerciseToDelete.exerciseId}" for section "${exerciseToDelete.sectionId}"?`
      )
    )
      return;
    setGeneralStatusMessage(
      `Deleting exercise ${exerciseToDelete.exerciseId}...`
    );
    try {
      await deleteReconstructExercise(
        exerciseToDelete.sectionId,
        exerciseToDelete.exerciseId
      );
      setGeneralStatusMessage(
        `Exercise ${exerciseToDelete.exerciseId} deleted successfully.`
      );
      setExistingReconstructExercises((prev) =>
        prev.filter((ex) => ex.exerciseId !== exerciseToDelete.exerciseId)
      );
    } catch (err: any) {
      console.error("Failed to delete exercise:", err);
      setGeneralStatusMessage(
        `Error deleting exercise ${exerciseToDelete.exerciseId}: ${err.message}`
      );
    }
  };

  // ── Handler for opening the Edit Modal for an existing exercise ──
  const handleOpenEditModal = (exercise: ReconstructExerciseDBRecord) => {
    setEditingExercise(exercise);
    const formData: EditModalFormState = { ...exercise };
    const fieldsToConvert: {
      jsonFieldKey: keyof ReconstructExerciseDBRecord;
      csvFieldKey: keyof EditModalFormState;
    }[] = [
      {
        jsonFieldKey: "wordsOrderedTarget",
        csvFieldKey: "_wordsOrderedTargetCsv",
      },
      {
        jsonFieldKey: "distractorsTarget",
        csvFieldKey: "_distractorsTargetCsv",
      },
      { jsonFieldKey: "wordsOrderedBase", csvFieldKey: "_wordsOrderedBaseCsv" },
      { jsonFieldKey: "distractorsBase", csvFieldKey: "_distractorsBaseCsv" },
    ];
    fieldsToConvert.forEach(({ jsonFieldKey, csvFieldKey }) => {
      let csvString = "";
      try {
        const jsonStringValue = exercise[jsonFieldKey];
        if (jsonStringValue && typeof jsonStringValue === "string") {
          const parsedArray = JSON.parse(jsonStringValue);
          if (Array.isArray(parsedArray)) {
            if (
              (jsonFieldKey === "wordsOrderedTarget" ||
                jsonFieldKey === "wordsOrderedBase") &&
              parsedArray.length > 0 &&
              Array.isArray(parsedArray[0])
            ) {
              // Convert array of arrays to "a,b|c,d"
              csvString = (parsedArray as any[][])
                .map((innerArray) =>
                  innerArray
                    .filter((item) => typeof item === "string")
                    .join(", ")
                )
                .join(" | ");
            } else if ((parsedArray as any[]).every((item) => typeof item === "string")) {
              csvString = (parsedArray as string[]).join(", ");
            }
          } else {
            console.warn(
              `Field ${jsonFieldKey} in exercise ${exercise.exerciseId} was not an array after parsing:`,
              parsedArray
            );
          }
        }
      } catch (e) {
        console.warn(
          `Could not parse JSON for field ${jsonFieldKey} in exercise ${exercise.exerciseId}:`,
          exercise[jsonFieldKey],
          e
        );
      }
      (formData as any)[csvFieldKey] = csvString;
    });
    setEditedExerciseData(formData);
    setIsEditModalOpen(true);
    setGeneralStatusMessage(null);
  };

  // ── Handler to close the Edit Modal ──
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingExercise(null);
    setEditedExerciseData({});
  };

  // ── AI Bulk Edit Handlers ──
  const handleOpenAIBulkEdit = () => {
    if (!editedExerciseData) return;
    setAIModalMode('edit');
    setShowAIBulkEditModal(true);
    setAIBulkEditPrompt("");
    setAIEditPreview(null);
  };

  const handleCloseAIBulkEdit = () => {
    setShowAIBulkEditModal(false);
    setAIBulkEditPrompt("");
    setAIEditPreview(null);
    setAIModalMode('edit');
    setIsProcessingAIEdit(false);
  };

  const handleProcessAIEdit = async () => {
    if (!aiBulkEditPrompt.trim()) {
      alert("Please provide edit instructions.");
      return;
    }

    // Validate based on mode
    if (aiModalMode === 'edit' && !editedExerciseData) {
      alert("No exercise data to edit.");
      return;
    }
    if (aiModalMode === 'generate' && (!currentContext || !currentContext.multiLanguageData)) {
      alert("No generated sentences to modify.");
      return;
    }

    console.log("Processing AI edit with instructions:", aiBulkEditPrompt);
    console.log("Mode:", aiModalMode);

    setIsProcessingAIEdit(true);
    try {
      let currentSentence: any;
      let targetLang: string;
      let currentSentenceTarget: string;
      let vocabularyWords: string[];

      if (aiModalMode === 'generate') {
        // Use data from currentContext when regenerating generated sentences
        const multiLangData = currentContext!.multiLanguageData!;
        targetLang = langCodeMap[sourceLanguage] || 'ES';
        currentSentenceTarget = multiLangData.sentenceTarget;

        // Extract vocabulary words from currentContext
        vocabularyWords = Array.from(currentContext!.selectedVocabularyWords.values())
          .map((item) => item.wordData.Targ_Word);

        currentSentence = {
          sentenceTarget: multiLangData.sentenceTarget,
          targetLanguage: targetLang,
        };
      } else {
        // Use editedExerciseData when editing existing exercises
        currentSentenceTarget = editedExerciseData.sentenceTarget || "";
        targetLang = editedExerciseData.targetLanguage || 'ES';

        // Extract vocabulary words from editedExerciseData.wordId
        vocabularyWords = [];
        if (editedExerciseData.wordId && vocabularyList.length > 0) {
          const wordIds = editedExerciseData.wordId.split(',').map((id: string) => id.trim());
          console.log("🔍 Extracting vocabulary words from wordId:", wordIds);

          // Search through vocabularyList to find matching vocabulary words
          for (const wordId of wordIds) {
            const matchingWord = vocabularyList.find((vocab) => vocab.Identifier === wordId);
            if (matchingWord && matchingWord.Targ_Word) {
              vocabularyWords.push(matchingWord.Targ_Word);
              console.log(`✅ Found vocabulary word for ${wordId}:`, matchingWord.Targ_Word);
            } else {
              console.warn(`⚠️ Could not find vocabulary word with ID: ${wordId}`);
            }
          }
          console.log("📚 Total vocabulary words extracted for edit mode:", vocabularyWords);
        } else if (editedExerciseData.wordId) {
          console.warn("⚠️ wordId exists but vocabularyList is empty. Cannot extract vocabulary words.");
        }

        currentSentence = {
          sentenceTarget: editedExerciseData.sentenceTarget || "",
          sentenceSource: editedExerciseData.sentenceSource || "",
          wordsOrderedTarget: editedExerciseData._wordsOrderedTargetCsv || "",
          wordsOrderedBase: editedExerciseData._wordsOrderedBaseCsv || "",
          distractorsTarget: editedExerciseData._distractorsTargetCsv || "",
          distractorsBase: editedExerciseData._distractorsBaseCsv || "",
          targetLanguage: editedExerciseData.targetLanguage || "",
          baseLanguage: editedExerciseData.baseLanguage || "",
        };
      }

      console.log("🤖 AI Edit Mode:", aiModalMode);
      console.log("📝 Current sentence data being sent to AI:", currentSentence);
      console.log("📚 Vocabulary words for AI:", vocabularyWords);
      console.log("📊 Vocabulary words count:", vocabularyWords.length);

      if (aiModalMode === 'generate' && vocabularyWords.length === 0) {
        console.error("❌ ERROR: In 'generate' mode but no vocabulary words found!");
        console.error("currentContext.selectedVocabularyWords.size:", currentContext?.selectedVocabularyWords.size);
      }

      // Build AI prompt using a simple structure
      const systemContent = `
You are an AI assistant specialized in generating sentence translation exercises for language learning applications.
Your task is to ${aiModalMode === 'edit' ? 'MODIFY an existing' : 'REGENERATE a'} sentence in the target language with translations to ALL common base languages.

${aiModalMode === 'edit' ? `CURRENT SENTENCE TO MODIFY:
Target Language (${targetLang}): "${currentSentenceTarget}"
Base Language Translation: "${currentSentence.sentenceSource || 'N/A'}"

IMPORTANT: Your modification should preserve the core meaning and context of the original sentence while applying the user's requested changes. Do NOT create a completely different sentence unless specifically requested.
` : `The current sentence is: "${currentSentenceTarget}"`}

${vocabularyWords.length > 0 && aiModalMode === 'generate' ? `
VOCABULARY FOCUS:
The sentence MUST incorporate AT LEAST ONE (ideally several) of the following vocabulary words as the focal point:
${vocabularyWords.map((word) => `- ${word}`).join("\n")}

These vocabulary words are what learners are studying. The sentence should naturally showcase these words in context.
` : vocabularyWords.length > 0 && aiModalMode === 'edit' ? `
VOCABULARY CONTEXT (for reference):
The original sentence was created to teach: ${vocabularyWords.join(", ")}
Note: You may include these words in your modification, but they are not required if the user's instructions suggest otherwise.
` : ''}

IMPORTANT RULES:
1. Generate ONE ${aiModalMode === 'edit' ? 'modified version of the existing' : 'regenerated'} sentence in the target language (${targetLang})
2. Provide translations of this sentence to ALL base languages: EN (English), FR (French), DE (German), IT (Italian), PT (Portuguese)
3. Create appropriate word segments (comma-separated words from the sentence) for each language
4. Generate relevant distractors (wrong words that could confuse learners) for each language
5. Ensure all sentences are natural and grammatically correct in their respective languages
6. Keep the same meaning across all language translations
7. Appropriate for language learners at the specified level
${aiModalMode === 'edit' ? '8. Apply the user\'s modification instructions while preserving the original sentence\'s core meaning and structure' : ''}

Output Format:
Respond ONLY with a valid JSON object matching this exact structure:
{
  "sentenceTarget": "${aiModalMode === 'generate' ? 'modified' : 'new'} sentence in target language",
  "translations": [
    {
      "baseLangCode": "EN",
      "sentenceSource": "sentence in English",
      "wordsOrderedBase": "word1,word2,word3",
      "distractorsBase": "distractor1,distractor2,distractor3"
    },
    {
      "baseLangCode": "FR",
      "sentenceSource": "sentence in French",
      "wordsOrderedBase": "mot1,mot2,mot3",
      "distractorsBase": "distractor1,distractor2,distractor3"
    },
    {
      "baseLangCode": "DE",
      "sentenceSource": "sentence in German",
      "wordsOrderedBase": "wort1,wort2,wort3",
      "distractorsBase": "distractor1,distractor2,distractor3"
    },
    {
      "baseLangCode": "IT",
      "sentenceSource": "sentence in Italian",
      "wordsOrderedBase": "parola1,parola2,parola3",
      "distractorsBase": "distractor1,distractor2,distractor3"
    },
    {
      "baseLangCode": "PT",
      "sentenceSource": "sentence in Portuguese",
      "wordsOrderedBase": "palavra1,palavra2,palavra3",
      "distractorsBase": "distractor1,distractor2,distractor3"
    }
  ],
  "wordsOrderedTarget": "word1,word2,word3 (comma-separated words from target sentence)",
  "distractorsTarget": "distractor1,distractor2,distractor3 (wrong words in target language)"
}`;

      const userContent = `
Target Language: ${targetLang}
Domain: ${aiModalMode === 'edit' ? (editedExerciseData.domain || 'general') : (domain || 'general')}
Proficiency Level: ${aiModalMode === 'edit' ? (editedExerciseData.proficiencyLevel || 'A1') : (proficiencyLevel || 'A1')}
Difficulty: ${aiModalMode === 'edit' ? (editedExerciseData.difficulty || '1') : (difficulty || '1')}
${vocabularyWords.length > 0 && aiModalMode === 'generate' ? `\nVocabulary Words to Include: ${vocabularyWords.join(", ")}` : ''}
${vocabularyWords.length > 0 && aiModalMode === 'edit' ? `\nOriginal Vocabulary Context: ${vocabularyWords.join(", ")}` : ''}

${aiModalMode === 'edit' ? 'Modification' : 'Regeneration'} Instructions: ${aiBulkEditPrompt}

Please generate ${aiModalMode === 'edit' ? 'a modified version of the existing sentence' : 'a regenerated sentence'} based on these instructions with translations to ALL base languages (EN, FR, DE, IT, PT) and return it in the exact JSON format specified.${aiModalMode === 'edit' ? ' Remember to preserve the core meaning and context of the original sentence while applying the requested changes.' : ''}`;

      const payload = {
        messages: [
          {
            role: "system",
            content: systemContent.trim(),
          },
          {
            role: "user",
            content: userContent.trim(),
          },
        ],
        temperature: 0.7,
        max_tokens: 2000,
      };

      // Call OpenAI
      const result = await callOpenAI(payload);
      if (!result || !result.response) {
        throw new Error("Failed to get AI response");
      }

      // Parse AI response
      let aiEditedData;
      try {
        // Check if result.response is already an object or a string
        if (typeof result.response === 'object' && result.response !== null) {
          // Response is already parsed
          aiEditedData = result.response;
        } else if (typeof result.response === 'string') {
          // Response is a string, need to parse it
          let jsonString = result.response;
          const startIndex = jsonString.indexOf("{");
          const endIndex = jsonString.lastIndexOf("}") + 1;

          if (startIndex !== -1 && endIndex > startIndex) {
            jsonString = jsonString.slice(startIndex, endIndex).trim();
            aiEditedData = JSON.parse(jsonString);
          } else {
            throw new Error("No valid JSON found in response");
          }
        } else {
          throw new Error("Unexpected response type");
        }
      } catch (parseError) {
        console.error("Failed to parse AI response:", parseError);
        console.error("Response was:", result.response);
        throw new Error("Invalid response format from AI");
      }

      // Validate AI response structure
      if (!aiEditedData.sentenceTarget || !aiEditedData.translations || !Array.isArray(aiEditedData.translations)) {
        throw new Error("AI response missing required fields or translations array");
      }

      console.log("AI edited data received:", aiEditedData);

      // Store the full AI response with all translations
      // We'll show all translations in preview and save them all
      (window as any).aiGeneratedTranslations = aiEditedData;

      // Find the translation that matches current base language for the preview
      const currentBaseLanguage = editedExerciseData.baseLanguage || 'EN';
      const matchingTranslation = aiEditedData.translations.find(
        (t: any) => t.baseLangCode === currentBaseLanguage
      ) || aiEditedData.translations[0]; // Default to first if not found

      // Create preview of edited data for the current base language
      const previewState: EditModalFormState = {
        ...editedExerciseData,
        sentenceTarget: aiEditedData.sentenceTarget,
        sentenceSource: matchingTranslation.sentenceSource,
        _wordsOrderedTargetCsv: aiEditedData.wordsOrderedTarget,
        _wordsOrderedBaseCsv: matchingTranslation.wordsOrderedBase,
        _distractorsTargetCsv: aiEditedData.distractorsTarget,
        _distractorsBaseCsv: matchingTranslation.distractorsBase,
      };

      console.log("Preview state created:", previewState);
      console.log("All translations available:", aiEditedData.translations.map((t: any) => t.baseLangCode).join(', '));
      setAIEditPreview(previewState);
    } catch (error: any) {
      console.error("Error processing AI edit:", error);
      alert(`Failed to process AI edit: ${error.message || "Unknown error"}`);
    } finally {
      setIsProcessingAIEdit(false);
    }
  };

  const handleApplyAIEdit = () => {
    if (!aiEditPreview) return;

    const aiData = (window as any).aiGeneratedTranslations;

    if (aiModalMode === 'generate') {
      // Apply to generated sentences in currentContext
      if (!currentContext || !aiData) return;

      console.log("Applying AI edits to generated sentences:", aiData);

      // Update the context with new multi-language data
      setSentenceContexts((prev) =>
        prev.map((ctx) =>
          ctx.id === currentContext.id
            ? {
                ...ctx,
                generatedSourceSentenceData: {
                  sentence: aiData.sentenceTarget,
                  correctWords: aiData.wordsOrderedTarget.split(',').map((w: string) => w.trim()),
                  distractorWords: aiData.distractorsTarget.split(',').map((w: string) => w.trim())
                },
                multiLanguageData: aiData // Update with new multi-language data
              }
            : ctx
        )
      );

      // Close AI edit modal
      handleCloseAIBulkEdit();

      console.log("AI-generated sentences applied to currentContext");
      alert("AI-generated sentences applied successfully! The review section now shows the updated sentences.");
    } else {
      // Original edit mode logic
      console.log("Applying AI edits to current exercise:", aiEditPreview);

      // Apply the AI-generated edits to the main edit form
      // This just updates the form fields - user still needs to click "Save Changes"
      setEditedExerciseData(aiEditPreview);

      // Close AI edit modal
      handleCloseAIBulkEdit();

      console.log("AI-generated sentence applied to editedExerciseData");
      alert("AI-generated sentence applied successfully! The form fields now show the new sentence. Review and click 'Save Changes' to update the exercise.");
    }
  };

  const handleUpdateAllVariations = async () => {
    if (!aiEditPreview) return;

    const aiData = (window as any).aiGeneratedTranslations;
    if (!aiData || !aiData.translations) {
      alert("Error: AI translation data not found");
      return;
    }

    console.log("handleUpdateAllVariations - editingExercise:", editingExercise);
    console.log("handleUpdateAllVariations - section:", section);

    if (!editingExercise) {
      alert("Error: No exercise being edited");
      return;
    }

    if (!section) {
      alert("Error: No section selected");
      return;
    }

    const confirmed = window.confirm(
      `This will update ALL ${aiData.translations.length} base language variations for this sentence. Continue?`
    );
    if (!confirmed) return;

    try {
      setIsProcessingAIEdit(true);

      // Find all existing exercises with the same target sentence
      const targetSentenceToMatch = editingExercise.sentenceTarget;
      console.log("🔍 Looking for exercises matching:");
      console.log("  - Target sentence:", targetSentenceToMatch);
      console.log("  - Target sentence type:", typeof targetSentenceToMatch, Array.isArray(targetSentenceToMatch) ? '(Array)' : '');
      console.log("  - Section ID:", editingExercise.sectionId);
      console.log("  - Total exercises in section:", existingReconstructExercises.length);

      // Normalize sentenceTarget for comparison (handle both string and array formats)
      const normalizeTarget = (target: any): string => {
        if (Array.isArray(target)) {
          return target[0] || '';
        }
        if (typeof target === 'string') {
          try {
            const parsed = JSON.parse(target);
            return Array.isArray(parsed) ? parsed[0] || '' : target;
          } catch {
            return target;
          }
        }
        return '';
      };

      const normalizedTargetToMatch = normalizeTarget(targetSentenceToMatch);
      console.log("  - Normalized target to match:", normalizedTargetToMatch);

      const relatedExercises = existingReconstructExercises.filter(ex => {
        const normalizedExTarget = normalizeTarget(ex.sentenceTarget);
        const matches = normalizedExTarget === normalizedTargetToMatch && ex.sectionId === editingExercise.sectionId;
        if (matches) {
          console.log(`  ✅ Found match: ${ex.baseLanguage} - exerciseId: ${ex.exerciseId}`);
        }
        return matches;
      });

      console.log("📊 Found related exercises:", relatedExercises.length);
      console.log("Related exercises details:", relatedExercises.map(ex => ({
        baseLanguage: ex.baseLanguage,
        exerciseId: ex.exerciseId
      })));

      // Update each related exercise with its corresponding translation
      console.log(`📝 Starting to update ${relatedExercises.length} exercises...`);
      const updatePromises = relatedExercises.map(async (exercise, index) => {
        console.log(`\n[${index + 1}/${relatedExercises.length}] Processing ${exercise.baseLanguage}...`);

        const translation = aiData.translations.find((t: any) => t.baseLangCode === exercise.baseLanguage);
        if (!translation) {
          console.warn(`❌ No translation found for ${exercise.baseLanguage}`);
          return null;
        }

        const updatePayload: Partial<ReconstructExerciseDBRecord> = {
          sentenceTarget: aiData.sentenceTarget,
          sentenceSource: translation.sentenceSource,
          wordsOrderedTarget: JSON.stringify([aiData.wordsOrderedTarget.split(',').map((w: string) => w.trim())]),
          wordsOrderedBase: JSON.stringify([translation.wordsOrderedBase.split(',').map((w: string) => w.trim())]),
          distractorsTarget: JSON.stringify(aiData.distractorsTarget ? aiData.distractorsTarget.split(',').map((w: string) => w.trim()) : []),
          distractorsBase: JSON.stringify(translation.distractorsBase ? translation.distractorsBase.split(',').map((w: string) => w.trim()) : []),
        };

        console.log(`  Updating exercise ID: ${exercise.exerciseId}`);
        console.log(`  Section: ${exercise.sectionId}`);
        console.log(`  Language: ${exercise.baseLanguage}`);
        console.log(`  New sentence: ${translation.sentenceSource}`);

        if (!exercise.sectionId || !exercise.exerciseId) {
          console.error("❌ Missing sectionId or exerciseId:", exercise);
          return null;
        }

        try {
          const result = await updateReconstructExercise(exercise.sectionId, exercise.exerciseId, updatePayload);
          console.log(`  ✅ Successfully updated ${exercise.baseLanguage}`);
          return result;
        } catch (error) {
          console.error(`  ❌ Failed to update ${exercise.baseLanguage}:`, error);
          return null;
        }
      });

      console.log(`\n⏳ Waiting for all ${updatePromises.length} updates to complete...`);
      const results = await Promise.all(updatePromises);
      const successfulUpdates = results.filter(r => r !== null).length;
      console.log(`✅ Completed ${successfulUpdates} of ${updatePromises.length} updates`);

      setGeneralStatusMessage(`Successfully updated ${successfulUpdates} sentence variations!`);

      // Refresh the exercises list
      const refreshedData = await fetchExistingReconstructExercises(section);
      setExistingReconstructExercises(refreshedData);

      // Close modals
      handleCloseAIBulkEdit();
      handleCloseEditModal();

      alert(`Successfully updated ${relatedExercises.length} sentence variations!`);

    } catch (error: any) {
      console.error("Error updating AI-generated exercises:", error);
      alert(`Failed to update exercises: ${error.message || "Unknown error"}`);
    } finally {
      setIsProcessingAIEdit(false);
    }
  };

  // ── Handler for any field changes in the Edit Modal ──
  const handleEditFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setEditedExerciseData((prev) => ({ ...prev, [name]: value }));
  };

  // ── Handler to actually send updates from the Edit Modal to the backend ──
  const handleUpdateEditedExercise = async () => {
    if (!editingExercise || Object.keys(editedExerciseData).length === 0) {
      setGeneralStatusMessage(
        "Error: No exercise data to update or no changes made."
      );
      return;
    }

    console.log("Starting update with editedExerciseData:", editedExerciseData);

    setGeneralStatusMessage(
      `Updating exercise ${editingExercise.exerciseId}...`
    );
    const updatePayload: Partial<ReconstructExerciseDBRecord> = {};
    for (const key in editedExerciseData) {
      if (
        Object.prototype.hasOwnProperty.call(editedExerciseData, key) &&
        !key.startsWith("_")
      ) {
        (updatePayload as any)[key] = (editedExerciseData as any)[key];
      }
    }

    console.log("Update payload before CSV processing:", updatePayload);
    const csvToProcess: {
      csvKey: keyof EditModalFormState;
      targetJsonKey: keyof ReconstructExerciseDBRecord;
    }[] = [
      { csvKey: "_wordsOrderedTargetCsv", targetJsonKey: "wordsOrderedTarget" },
      { csvKey: "_distractorsTargetCsv", targetJsonKey: "distractorsTarget" },
      { csvKey: "_wordsOrderedBaseCsv", targetJsonKey: "wordsOrderedBase" },
      { csvKey: "_distractorsBaseCsv", targetJsonKey: "distractorsBase" },
    ];
    for (const { csvKey, targetJsonKey } of csvToProcess) {
      const csvString = (editedExerciseData as any)[csvKey];
      if (typeof csvString === "string") {
        if (
          targetJsonKey === "wordsOrderedTarget" ||
          targetJsonKey === "wordsOrderedBase"
        ) {
          const arrayOfWordArrays = csvString
            .split("|")
            .map((line) =>
              line
                .split(",")
                .map(stripPunctuationFromSegment)
                .filter((item) => item.length > 0)
            )
            .filter((arr) => arr.length > 0);
          (updatePayload as any)[targetJsonKey] = JSON.stringify(
            arrayOfWordArrays.length > 0 ? arrayOfWordArrays : []
          );
        } else {
          const itemsArray = csvString
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
          (updatePayload as any)[targetJsonKey] = JSON.stringify(itemsArray);
        }
      } else if (
        editingExercise[targetJsonKey] &&
        (editedExerciseData as any)[csvKey] === undefined
      ) {
        (updatePayload as any)[targetJsonKey] = editingExercise[targetJsonKey];
      } else {
        (updatePayload as any)[targetJsonKey] = JSON.stringify([]);
      }
    }

    console.log("Final update payload being sent to backend:", updatePayload);

    try {
      await updateReconstructExercise(
        editingExercise.sectionId,
        editingExercise.exerciseId,
        updatePayload
      );
      setGeneralStatusMessage(
        `Exercise ${editingExercise.exerciseId} updated successfully.`
      );
      setExistingReconstructExercises((prev) =>
        prev.map((ex) =>
          ex.exerciseId === editingExercise.exerciseId
            ? { ...editingExercise, ...updatePayload }
            : ex
        )
      );
      handleCloseEditModal();
    } catch (err: any) {
      console.error("Failed to update exercise:", err);
      setGeneralStatusMessage(
        `Error updating exercise ${editingExercise.exerciseId}: ${err.message}`
      );
    }
  };

  // ── Handler to delete all variations of a sentence ──
  const handleDeleteAllVariations = async (variations: ReconstructExerciseDBRecord[]) => {
    if (!variations || variations.length === 0) return;

    const targetSentence = variations[0].sentenceTarget;
    const confirmed = window.confirm(
      `This will DELETE ALL ${variations.length} base language variations of this sentence:\n\n"${targetSentence}"\n\nThis action cannot be undone. Continue?`
    );

    if (!confirmed) return;

    try {
      setGeneralStatusMessage(`Deleting ${variations.length} sentence variations...`);

      const deletePromises = variations.map((exercise) =>
        deleteReconstructExercise(exercise.sectionId, exercise.exerciseId)
      );

      await Promise.all(deletePromises);

      setGeneralStatusMessage(`Successfully deleted ${variations.length} sentence variations!`);

      // Remove all deleted exercises from the list
      setExistingReconstructExercises((prev) =>
        prev.filter((ex) => !variations.some((v) => v.exerciseId === ex.exerciseId))
      );

      setTimeout(() => setGeneralStatusMessage(null), 3000);
    } catch (err: any) {
      console.error("Failed to delete all variations:", err);
      setGeneralStatusMessage(`Error deleting variations: ${err.message}`);
    }
  };

  // ── SentenceCarousel Component ──
  const SentenceCarousel: React.FC<{
    variations: ReconstructExerciseDBRecord[];
    onEdit: (exercise: ReconstructExerciseDBRecord) => void;
    onDelete: (exercise: ReconstructExerciseDBRecord) => void;
    onDeleteAll: (variations: ReconstructExerciseDBRecord[]) => void;
  }> = ({ variations, onEdit, onDelete, onDeleteAll }) => {
    // Find initial index: prioritize "EN"
    const initialIndex = useMemo(() => {
      if (!variations || variations.length === 0) return 0;
      const enIndex = variations.findIndex((v) => v.baseLanguage === "EN");
      return enIndex !== -1 ? enIndex : 0;
    }, [variations]);

    const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);

    // Reset index when variations change
    useEffect(() => {
      setCurrentIndex(initialIndex);
    }, [initialIndex]);

    const handleCarouselChange = (direction: "prev" | "next") => {
      if (!variations || variations.length <= 1) return;
      setCurrentIndex((prev) => {
        const newIndex =
          direction === "next"
            ? (prev + 1) % variations.length
            : (prev - 1 + variations.length) % variations.length;
        return newIndex;
      });
    };

    if (!variations || variations.length === 0) {
      return <div className={styles.carousel}>No variations.</div>;
    }

    const currentVariation =
      currentIndex < variations.length ? variations[currentIndex] : null;

    if (!currentVariation) {
      return <div className={styles.carousel}>Error loading variation.</div>;
    }

    return (
      <div className={styles.carousel}>
        <button
          onClick={() => handleCarouselChange("prev")}
          className={styles.carouselButton}
          disabled={variations.length <= 1}
          aria-label="Previous variation"
        >
          ←
        </button>
        <div className={styles.carouselContent}>
          <div style={{ marginBottom: "10px" }}>
            <strong>Base Sentence ({currentVariation.baseLanguage}):</strong>
            <div>{currentVariation.sentenceSource}</div>
          </div>
          <div style={{ fontSize: "0.85em", color: "#666" }}>
            <div><strong>Domain:</strong> {currentVariation.domain}</div>
            <div><strong>Proficiency:</strong> {currentVariation.proficiencyLevel}</div>
            <div><strong>Difficulty:</strong> {currentVariation.difficulty}</div>

            {/* Display Distractor Words */}
            {(() => {
              // Helper function to parse distractors - handles arrays, JSON strings, and CSV strings
              const parseDistractors = (raw: any): string[] => {
                if (!raw) return [];

                // If it's already an array, just filter and return
                if (Array.isArray(raw)) {
                  return raw.filter(item => item && typeof item === 'string' && item.trim());
                }

                // If it's not a string, return empty
                if (typeof raw !== 'string') return [];

                // If empty string, return empty
                if (raw.trim() === '') return [];

                // Try parsing as JSON array
                try {
                  const parsed = JSON.parse(raw);
                  if (Array.isArray(parsed)) {
                    return parsed.filter(item => item && typeof item === 'string' && item.trim());
                  }
                } catch {
                  // Not valid JSON, continue to CSV parsing
                }

                // Parse as comma-separated string
                return raw
                  .split(',')
                  .map(item => item.trim())
                  .filter(item => item.length > 0);
              };

              // Parse target distractors
              const targetDistractors = parseDistractors(currentVariation.distractorsTarget);

              // Parse base distractors
              const baseDistractors = parseDistractors(currentVariation.distractorsBase);

              return (
                <>
                  {targetDistractors.length > 0 && (
                    <div style={{ marginTop: "8px" }}>
                      <strong style={{ color: "#2a7f2a" }}>
                        {currentVariation.targetLanguage} Distractors:
                      </strong>{" "}
                      <span style={{ color: "#555" }}>{targetDistractors.join(", ")}</span>
                    </div>
                  )}
                  {baseDistractors.length > 0 && (
                    <div style={{ marginTop: "4px" }}>
                      <strong style={{ color: "#1565c0" }}>
                        {currentVariation.baseLanguage} Distractors:
                      </strong>{" "}
                      <span style={{ color: "#555" }}>{baseDistractors.join(", ")}</span>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          <div style={{ marginTop: "10px", display: "flex", gap: "5px", flexWrap: "wrap" }}>
            <button
              onClick={() => onEdit(currentVariation)}
              className={`${styles.button} ${styles.editButton}`}
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(currentVariation)}
              className={`${styles.button} ${styles.deleteButton}`}
            >
              Delete
            </button>
            {variations.length > 1 && (
              <button
                onClick={() => onDeleteAll(variations)}
                className={`${styles.button}`}
                style={{
                  backgroundColor: "#d32f2f",
                  color: "white",
                  fontSize: "0.85em",
                  padding: "6px 12px"
                }}
                title={`Delete all ${variations.length} language variations`}
              >
                Delete All ({variations.length})
              </button>
            )}
          </div>
        </div>
        <button
          onClick={() => handleCarouselChange("next")}
          className={styles.carouselButton}
          disabled={variations.length <= 1}
          aria-label="Next variation"
        >
          →
        </button>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Vocabulary Sentence Generator & Manager</h1>
      {error && (
        <div className={`${styles.error} ${styles.stickyError}`}>{error}</div>
      )}
      {generalStatusMessage && (
        <div className={`${styles.statusMessage} ${styles.stickyStatus}`}>
          {generalStatusMessage}
        </div>
      )}
      
      {/* ── Sentence Tabs (now only showing sentence buttons) ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "20px",
          gap: "10px",
        }}
      >
        {sentenceContexts.map((ctx) => (
          <button
            key={ctx.id}
            onClick={() => handleSelectSentence(ctx.id)}
            className={
              ctx.id === currentSentenceId
                ? `${styles.button} ${styles.activeSentence}`
                : styles.button
            }
            disabled={generatingSentence || translatingSentences || isSavingPrepared}
          >
            {ctx.name}
          </button>
        ))}
      </div>
      
      {/* ── Shared Controls: Language / Section / Domain / Proficiency / Difficulty ── */}
      <LanguageControlsGrid
        sourceLanguage={sourceLanguage}
        section={section}
        domain={domain}
        proficiencyLevel={proficiencyLevel}
        difficulty={difficulty}
        isGenerating={generatingSentence}
        isTranslating={translatingSentences}
        isSaving={isSavingPrepared}
        loadingVocabulary={loadingVocabulary}
        onSourceLanguageChange={handleSourceLanguageChange}
        onSectionChange={handleSectionChange}
        onDomainChange={handleDomainChange}
        onProficiencyChange={handleProficiencyChange}
        onDifficultyChange={handleDifficultyChange}
        onSectionsLoaded={handleSectionsLoaded}
        error={error}
      />
      
      {/* ── New Add Sentence Button placed below the shared controls ── */}
      <div style={{ marginTop: "10px" }}>
        <button
          onClick={handleAddSentence}
          className={`${styles.button} ${styles.buttonMinor}`}
          disabled={
            generatingSentence ||
            translatingSentences ||
            isSavingPrepared ||
            !section ||
            loadingVocabulary
          }
          title="Add a new sentence context"
        >
          + Add Sentence
        </button>
      </div>
      
      {sourceLanguage && section && (
        <div className={styles.sectionContainer}>
          <h2 className={styles.subHeading}>
            Select Vocabulary from "{section}" ({sourceLanguage})
          </h2>
          {loadingVocabulary && (
            <p className={styles.loadingMessage}>Loading vocabulary...</p>
          )}
          {!loadingVocabulary && vocabularyList.length === 0 && (
            <p className={styles.infoMessage}>
              No vocabulary words found for this section.
            </p>
          )}
          {!loadingVocabulary && vocabularyList.length > 0 && (
            <div className={styles.vocabList}>
              {vocabularyList.map((vocabItem) => (
                <div key={vocabItem.Identifier} className={styles.vocabItem}>
                  <input
                    type="checkbox"
                    id={`vocab-${vocabItem.Identifier}-${currentContext?.id || ""}`}
                    checked={currentContext?.selectedVocabularyWords.has(
                      vocabItem.Identifier
                    ) || false}
                    onChange={() => handleVocabWordSelection(vocabItem)}
                    disabled={
                      !currentContext ||
                      generatingSentence ||
                      translatingSentences ||
                      isSavingPrepared
                    }
                  />
                  <label
                    htmlFor={`vocab-${vocabItem.Identifier}-${
                      currentContext?.id || ""
                    }`}
                  >
                    {vocabItem.Targ_Word}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── If there is a current sentence selected, show its currently selected words ── */}
      {currentContext && currentContext.selectedVocabularyWords.size > 0 && (
        <div
          className={styles.sectionContainer}
          style={{
            marginTop: "20px",
            borderTop: "1px solid #ccc",
            paddingTop: "20px",
          }}
        >
          <h3 className={styles.subHeading}>
            {currentContext.name} ‒ Currently Selected Words (
            {currentContext.selectedVocabularyWords.size})
          </h3>
          <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
            {Array.from(currentContext.selectedVocabularyWords.values()).map(
              ({ wordData, sourceSection }) => {
                // Parse options/distractors
                let targOptions: string[] = [];
                let baseOptions: string[] = [];
                try {
                  targOptions = JSON.parse(wordData.Targ_Lang_Options || '[]');
                } catch {
                  targOptions = [];
                }
                try {
                  baseOptions = JSON.parse(wordData.Base_Lang_Options || '[]');
                } catch {
                  baseOptions = [];
                }

                return (
                  <li
                    key={wordData.Identifier}
                    style={{
                      marginBottom: "15px",
                      padding: "12px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        {/* Main word display */}
                        <div style={{ marginBottom: "8px" }}>
                          <strong style={{ fontSize: "16px", color: "#1976d2" }}>
                            {wordData.Targ_Word}
                          </strong>
                          <span style={{ marginLeft: "8px", color: "#666" }}>
                            ({wordData.Base_Word})
                          </span>
                        </div>

                        {/* Metadata */}
                        <div style={{ fontSize: "13px", color: "#555", marginBottom: "6px" }}>
                          <strong>Section:</strong> {sourceSection.sectionNameForAPI}
                          {' • '}
                          <strong>Level:</strong> {wordData.Level || 'N/A'}
                          {' • '}
                          <strong>ID:</strong> {sourceSection.identifier}
                        </div>

                        {/* Target Language Options (Distractors) */}
                        {targOptions.length > 0 && (
                          <div style={{ fontSize: "13px", marginBottom: "4px" }}>
                            <strong style={{ color: "#2a7f2a" }}>
                              {wordData.Targ_Lang_Code || 'Target'} Options:
                            </strong>
                            <span style={{ marginLeft: "6px", color: "#666" }}>
                              {targOptions.join(", ")}
                            </span>
                          </div>
                        )}

                        {/* Base Language Options (Distractors) */}
                        {baseOptions.length > 0 && (
                          <div style={{ fontSize: "13px" }}>
                            <strong style={{ color: "#1565c0" }}>
                              {wordData.Base_Lang_Code || 'Base'} Options:
                            </strong>
                            <span style={{ marginLeft: "6px", color: "#666" }}>
                              {baseOptions.join(", ")}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Remove button */}
                      <button
                        onClick={() => handleVocabWordSelection(wordData)}
                        className={styles.buttonMinor}
                        style={{
                          marginLeft: "15px",
                          padding: "6px 12px",
                          fontSize: "0.9em",
                          flexShrink: 0,
                        }}
                        disabled={
                          generatingSentence ||
                          translatingSentences ||
                          isSavingPrepared
                        }
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                );
              }
            )}
          </ul>
        </div>
      )}

      {/* ── AI Feedback/Instructions for Sentence Generation ── */}
      {currentContext && sourceLanguage && (
        <div style={{ marginTop: "15px", marginBottom: "10px" }}>
          <label htmlFor="sentenceGenerationPrompt" style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
            AI Instructions (optional):
          </label>
          <textarea
            id="sentenceGenerationPrompt"
            value={sentenceGenerationPrompt}
            onChange={(e) => setSentenceGenerationPrompt(e.target.value)}
            placeholder="Provide additional instructions for the AI (e.g., 'Make it about food', 'Use formal tone', 'Focus on past tense')"
            style={{
              width: "100%",
              minHeight: "60px",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontFamily: "inherit",
              fontSize: "14px"
            }}
            disabled={generatingSentence || translatingSentences || isSavingPrepared}
          />
        </div>
      )}

      {/* ── Button to Generate Source Sentence for the current context ── */}
      {currentContext && sourceLanguage && (
        <div className={styles.buttonContainer}>
          <button
            onClick={handleGenerateSourceSentence}
            disabled={
              !currentContext ||
              currentContext.selectedVocabularyWords.size === 0 ||
              generatingSentence ||
              translatingSentences ||
              loadingVocabulary ||
              !section ||
              isSavingPrepared
            }
            className={styles.button}
          >
            {generatingSentence
              ? "Generating..."
              : `Generate Source Sentence (${currentContext.selectedVocabularyWords.size})`}
          </button>
        </div>
      )}

      {generatingSentence && !currentContext?.editableSourceBundleForm && (
        <p className={styles.loadingMessage}>Generating source sentence...</p>
      )}

      {/* ── EDITABLE SOURCE SENTENCE SECTION for current context ── */}
      {currentContext && currentContext.editableSourceBundleForm && (
        <div
          className={styles.resultsArea}
          style={{
            border: "2px solid dodgerblue",
            padding: "15px",
            marginTop: "20px",
            backgroundColor: "#e9f5ff",
          }}
        >
          <h3 className={styles.subHeading} style={{ color: "dodgerblue" }}>
            Review & Edit Generated Source Content ({sourceLanguage}) ‒{" "}
            {currentContext.name}
          </h3>

          {/* Target Language Sentence */}
          {currentContext.generatedSourceSentenceData && (
            <div
              style={{
                border: "2px solid #2a7f2a",
                padding: "15px",
                marginBottom: "20px",
                backgroundColor: "#e8f5e9",
                borderRadius: "6px",
              }}
            >
              <h4 style={{ marginTop: 0, marginBottom: "10px", color: "#2a7f2a" }}>
                <span style={{
                  backgroundColor: "#2a7f2a",
                  color: "white",
                  padding: "3px 10px",
                  borderRadius: "4px",
                  marginRight: "8px",
                  fontSize: "0.9em"
                }}>
                  {langCodeMap[sourceLanguage] || sourceLanguage}
                </span>
                Target Sentence ({sourceLanguage})
              </h4>

              <div style={{ marginBottom: "10px" }}>
                <strong style={{ color: "#1b5e20" }}>Sentence:</strong>
                <div style={{
                  marginTop: "5px",
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: "#2a7f2a",
                  padding: "10px",
                  backgroundColor: "white",
                  borderRadius: "4px",
                  border: "1px solid #4caf50"
                }}>
                  {currentContext.generatedSourceSentenceData.sentence}
                </div>
              </div>

              <div style={{ marginBottom: "10px" }}>
                <strong style={{ color: "#1b5e20" }}>Word Segments:</strong>
                <div style={{
                  marginTop: "5px",
                  padding: "8px",
                  backgroundColor: "white",
                  borderRadius: "4px",
                  border: "1px solid #4caf50"
                }}>
                  {currentContext.generatedSourceSentenceData.correctWords.join(", ")}
                </div>
              </div>

              <div>
                <strong style={{ color: "#1b5e20" }}>Distractors:</strong>
                <div style={{
                  marginTop: "5px",
                  padding: "8px",
                  backgroundColor: "white",
                  borderRadius: "4px",
                  border: "1px solid #4caf50"
                }}>
                  {currentContext.generatedSourceSentenceData.distractorWords.join(", ") || <em style={{ color: '#888' }}>None</em>}
                </div>
              </div>
            </div>
          )}

          <h4 style={{ marginBottom: "15px", color: "#555" }}>Base Language Translations:</h4>
          {currentContext.editableSourceBundleForm.variations.map(
            (variation, index) => (
              <div
                key={variation.id}
                style={{
                  border: "1px solid #b0c4de",
                  padding: "10px",
                  marginBottom: "10px",
                  backgroundColor: index === 0 ? "#fff8e1" : "white",
                  borderRadius: "4px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "5px",
                  }}
                >
                  <h4>
                    {variation.baseLangCode ? (
                      <>
                        <span style={{
                          backgroundColor: "#1976d2",
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          marginRight: "8px",
                          fontSize: "0.9em"
                        }}>
                          {variation.baseLangCode}
                        </span>
                        Sentence Variation {index + 1}
                      </>
                    ) : (
                      <>Sentence Variation {index + 1}</>
                    )}{" "}
                    {index === 0 && (
                      <em style={{ fontWeight: "normal", color: "#555" }}>
                        (Primary for Translation API)
                      </em>
                    )}
                  </h4>
                  {currentContext.editableSourceBundleForm?.variations.map((variation, index) => (
                    <button
                      onClick={() =>
                        handleRemoveVariation(variation.id)
                      }
                      className={`${styles.buttonMinor} ${styles.deleteButtonSmall}`}
                      disabled={translatingSentences || isSavingPrepared}
                    >
                      Remove Variation
                    </button>
                  ))}
                </div>
                <div className={styles.formControlModal}>
                  <label
                    htmlFor={`variationText-${variation.id}`}
                    className={styles.label}
                  >
                    Sentence Text:
                  </label>
                  <input
                    type="text"
                    id={`variationText-${variation.id}`}
                    value={variation.text}
                    onChange={(e) =>
                      handleVariationTextChange(
                        variation.id,
                        e.target.value
                      )
                    }
                    className={styles.input}
                    style={{ marginBottom: "5px" }}
                    disabled={translatingSentences || isSavingPrepared}
                  />
                </div>
                <div className={styles.formControlModal}>
                  <label
                    htmlFor={`variationCorrectWords-${variation.id}`}
                    className={styles.label}
                  >
                    Correct Word Segments (comma-separated):
                  </label>
                  <textarea
                    id={`variationCorrectWords-${variation.id}`}
                    value={variation.correctWordsCsv}
                    onChange={(e) =>
                      handleVariationCorrectWordsCsvChange(
                        variation.id,
                        e.target.value
                      )
                    }
                    className={styles.input}
                    rows={2}
                    placeholder="e.g., Tu amiga, ,, habla español, ?"
                    style={{ marginBottom: "5px" }}
                    disabled={translatingSentences || isSavingPrepared}
                  />
                </div>
              </div>
            )
          )}
          <button
            onClick={handleAddVariation}
            className={`${styles.button} ${styles.buttonMinor}`}
            style={{ marginTop: "5px", marginBottom: "15px" }}
            disabled={translatingSentences || isSavingPrepared}
          >
            + Add Sentence Variation
          </button>
          <div className={styles.formControlModal}>
            <label htmlFor="distractorWordsCsv" className={styles.label}>
              Common Distractor Words (comma-separated):
            </label>
            <textarea
              id="distractorWordsCsv"
              name="distractorWordsCsv"
              value={currentContext.editableSourceBundleForm.distractorWordsCsv}
              onChange={(e) =>
                handleDistractorWordsCsvChange(e.target.value)
              }
              className={styles.input}
              rows={2}
              placeholder="e.g., distractor1, distractor2"
              style={{ marginBottom: "10px" }}
              disabled={translatingSentences || isSavingPrepared}
            />
          </div>

          {/* AI Regeneration Button */}
          <div style={{ marginTop: "15px", marginBottom: "10px" }}>
            <button
              onClick={handleOpenAIRegenerationModal}
              disabled={translatingSentences || isSavingPrepared}
              style={{
                backgroundColor: "#ff9800",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "4px",
                cursor: translatingSentences || isSavingPrepared ? "not-allowed" : "pointer",
                opacity: translatingSentences || isSavingPrepared ? 0.6 : 1,
                fontWeight: "bold",
                fontSize: "14px",
                width: "100%"
              }}
            >
              🤖 AI Bulk Edit - Modify Generated Sentences
            </button>
          </div>

          <div
            className={styles.buttonContainer}
            style={{
              marginTop: "20px",
              borderTop: "1px solid #ccc",
              paddingTop: "20px",
            }}
          >
            <button
              onClick={handleTranslateAndPrepareExercises}
              disabled={
                translatingSentences ||
                (!currentContext.editableSourceBundleForm) ||
                currentContext.editableSourceBundleForm.variations
                  .length === 0 ||
                !currentContext.editableSourceBundleForm.variations[0].text.trim()
              }
              className={`${styles.button} ${styles.actionButton}`}
              title={
                currentContext.multiLanguageData && currentContext.multiLanguageData.translations?.length === 5
                  ? "Prepare exercises from AI-generated translations (no translation needed)."
                  : "Validates current edits, then translates the primary sentence and prepares exercises with all variations."
              }
            >
              {translatingSentences
                ? (currentContext.multiLanguageData && currentContext.multiLanguageData.translations?.length === 5 ? "Preparing..." : "Translating...")
                : (currentContext.multiLanguageData && currentContext.multiLanguageData.translations?.length === 5
                    ? "Save Exercises"
                    : "Translate & Prepare Exercises")}
            </button>
          </div>
        </div>
      )}
      {/* End Editable Source Sentence Section */}

      {translatingSentences && (
        <p className={styles.loadingMessage}>Translating sentences...</p>
      )}

      {/* ── Prepared Exercises for current context ── */}
      {currentContext &&
        currentContext.preparedReconstructExercises.length > 0 && (
          <div className={styles.resultsArea}>
            <h3 className={styles.subHeading}>
              Prepared Reconstruct Exercises for {currentContext.name} (
              {currentContext.preparedReconstructExercises.length} records)
            </h3>
            <div
              className={styles.buttonContainer}
              style={{ marginBottom: "10px" }}
            >
              <button
                onClick={handleSaveAllPreparedExercises}
                className={`${styles.button} ${styles.actionButton}`}
                disabled={isSavingPrepared}
              >
                {isSavingPrepared
                  ? "Saving All..."
                  : `Save All ${currentContext.preparedReconstructExercises.length} Prepared Exercises`}
              </button>
            </div>
            {currentContext.preparedReconstructExercises.map((ex) => (
              <details
                key={ex.exerciseId + "-prepared"}
                className={styles.exerciseDetails}
                style={{
                  borderBottom: "1px dashed #eee",
                  paddingBottom: "10px",
                  marginBottom: "10px",
                }}
              >
                <summary>
                  <strong>Prepared:</strong> {ex.targetLanguage} &lt;-&gt;{" "}
                  {ex.baseLanguage} (ID: {ex.exerciseId.substring(0, 8)}) Section:{" "}
                  {ex.sectionId}
                </summary>
                <p>
                  <strong>Target Sentence(s):</strong>{" "}
                  <pre>
                    {JSON.stringify(
                      JSON.parse(ex.sentenceTarget),
                      null,
                      2
                    )}
                  </pre>
                </p>
                <p>
                  <strong>Base (Translated Primary):</strong> {ex.sentenceSource}
                </p>
                <details style={{ marginLeft: "20px" }}>
                  <summary>View Segmented Words & Distractors</summary>
                  <p>
                    Words Ordered Target Variations:{" "}
                    <pre>
                      {JSON.stringify(
                        JSON.parse(ex.wordsOrderedTarget),
                        null,
                        2
                      )}
                    </pre>
                  </p>
                  <p>
                    Distractors Target (Common):{" "}
                    <pre>
                      {JSON.stringify(
                        JSON.parse(ex.distractorsTarget),
                        null,
                        2
                      )}
                    </pre>
                  </p>
                  <p>
                    Words Ordered Base (for Primary):{" "}
                    <pre>
                      {JSON.stringify(
                        JSON.parse(ex.wordsOrderedBase),
                        null,
                        2
                      )}
                    </pre>
                  </p>
                  <p>
                    Distractors Base (for Primary):{" "}
                    <pre>
                      {JSON.stringify(
                        JSON.parse(ex.distractorsBase),
                        null,
                        2
                      )}
                    </pre>
                  </p>
                </details>
              </details>
            ))}
          </div>
        )}

      {/* ── Existing Reconstruct Exercises (global per‐section) ── */}
      {section && (
        <div className={styles.sectionContainer}>
          <h2 className={styles.subHeading}>
            Existing Reconstruct Exercises for "{section}"
          </h2>
          
          {/* Base Language Filter */}
          {existingReconstructExercises.length > 0 && (
            <div className={styles.filterContainer}>
              <div className={styles.formControl}>
                <label htmlFor="baseLanguageFilter" className={styles.label}>
                  Filter by Base Language:
                </label>
                <select
                  id="baseLanguageFilter"
                  value={baseLanguageFilter}
                  onChange={handleBaseLanguageFilterChange}
                  className={styles.input}
                >
                  <option value="">All Base Languages</option>
                  {uniqueBaseLanguages.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {loadingExistingExercises && (
            <p className={styles.loadingMessage}>
              Loading existing exercises...
            </p>
          )}
          {existingExercisesError && (
            <div className={styles.error}>{existingExercisesError}</div>
          )}
          {!loadingExistingExercises &&
            !existingExercisesError &&
            existingReconstructExercises.length === 0 && (
              <p className={styles.infoMessage}>
                No existing reconstruct exercises found for this section.
              </p>
            )}
          {!loadingExistingExercises &&
            !existingExercisesError &&
            filteredExercises.length === 0 &&
            existingReconstructExercises.length > 0 && (
              <p className={styles.infoMessage}>
                No exercises found for the selected base language filter.
              </p>
            )}
          {!loadingExistingExercises &&
            !existingExercisesError &&
            groupedSentences.length > 0 && (
              <div className={styles.tableContainer}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Word / Phrase</th>
                      <th>Base Language Variations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedSentences.map((group, index) => (
                      <tr key={`group-${index}`}>
                        <td style={{ verticalAlign: "top", maxWidth: "400px" }}>
                          <div style={{ fontWeight: "bold", marginBottom: "8px", fontSize: "18px", color: "#1976d2" }}>
                            {group.word}
                          </div>
                          <div style={{ fontWeight: "500", marginBottom: "5px", fontSize: "14px" }}>
                            {group.targetSentence}
                          </div>
                          <div style={{ fontSize: "0.85em", color: "#666" }}>
                            {group.variations.length} variation{group.variations.length !== 1 ? 's' : ''}
                          </div>
                        </td>
                        <td>
                          <SentenceCarousel
                            variations={group.variations}
                            onEdit={handleOpenEditModal}
                            onDelete={handleDeleteExistingExercise}
                            onDeleteAll={handleDeleteAllVariations}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {/* ── Edit Modal (unchanged) ── */}
      {isEditModalOpen && editingExercise && (
        <div className={styles.modalOverlay}>
          <div
            className={styles.modalContent}
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            <h2>
              Edit Reconstruct Exercise (ID:{" "}
              {editingExercise.exerciseId.substring(0, 8)})
            </h2>
            <div className={styles.modalForm}>
              {[
                {
                  name: "sentenceTarget",
                  label: "Target Sentence(s) (JSON Array String)",
                  type: "textarea",
                  note: 'Manually edit as valid JSON array of strings: ["S1", "S2"]',
                },
                {
                  name: "sentenceSource",
                  label: "Base Sentence (Translated Primary)",
                  type: "textarea",
                },
                {
                  name: "_wordsOrderedTargetCsv",
                  label: "Words Ordered Target (CSV for variations)",
                  type: "textarea",
                  placeholder: "s1w1,s1w2|s2w1,s2w2",
                  note: "Use '|' to separate word lists for each sentence variation. Matches 'Target Sentence(s)'.",
                },
                {
                  name: "_distractorsTargetCsv",
                  label: "Distractors Target (Common, comma‐sep)",
                  type: "textarea",
                  placeholder: "distractor1, distractor2",
                },
                {
                  name: "_wordsOrderedBaseCsv",
                  label:
                    "Words Ordered Base (for Translated Primary, comma‐sep)",
                  type: "textarea",
                  placeholder: "word1, phrase 2",
                },
                {
                  name: "_distractorsBaseCsv",
                  label: "Distractors Base (for Translated Primary, comma‐sep)",
                  type: "textarea",
                  placeholder: "distractor1, distractor2",
                },
                {
                  name: "targetLanguage",
                  label: "Target Language Code",
                  type: "text",
                },
                {
                  name: "baseLanguage",
                  label: "Base Language Code",
                  type: "text",
                },
                {
                  name: "domain",
                  label: "Domain",
                  type: "select",
                  options: DOMAINS,
                },
                {
                  name: "proficiencyLevel",
                  label: "Proficiency Level",
                  type: "select",
                  options: PROFICIENCY_LEVELS,
                },
                {
                  name: "difficulty",
                  label: "Difficulty",
                  type: "select",
                  options: DIFFICULTIES,
                },
                {
                  name: "wordId",
                  label: "Word ID(s) (comma‐sep)",
                  type: "text",
                },
              ].map((field) => (
                <div className={styles.formControlModal} key={field.name}>
                  <label htmlFor={field.name as string}>{field.label}:</label>
                  {field.type === "textarea" ? (
                    <textarea
                      id={field.name as string}
                      name={field.name as string}
                      value={
                        (editedExerciseData as any)[field.name as string] || ""
                      }
                      onChange={handleEditFormChange}
                      rows={
                        field.name.includes("Sentence") ||
                        field.name.includes("Csv")
                          ? 3
                          : 2
                      }
                      placeholder={field.placeholder || ""}
                      className={styles.input}
                    />
                  ) : field.type === "select" ? (
                    <select
                      id={field.name as string}
                      name={field.name as string}
                      value={
                        (editedExerciseData as any)[field.name as string] || ""
                      }
                      onChange={handleEditFormChange}
                      className={styles.input}
                    >
                      <option value="">{`Select ${field.label}`}</option>
                      {field.options?.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type as string}
                      id={field.name as string}
                      name={field.name as string}
                      value={
                        (editedExerciseData as any)[field.name as string] || ""
                      }
                      onChange={handleEditFormChange}
                      placeholder={field.placeholder || ""}
                      className={styles.input}
                    />
                  )}
                  {field.note && (
                    <small style={{ display: "block", marginTop: "3px" }}>
                      {field.note}
                    </small>
                  )}
                </div>
              ))}
            </div>
            <div className={styles.modalActions}>
              <button
                onClick={handleOpenAIBulkEdit}
                className={`${styles.button}`}
                style={{ backgroundColor: '#4a90e2', color: 'white' }}
              >
                🤖 Generate with AI
              </button>
              <button
                onClick={handleUpdateEditedExercise}
                className={`${styles.button} ${styles.saveButton}`}
              >
                Save Changes
              </button>
              <button
                onClick={handleCloseEditModal}
                className={`${styles.button} ${styles.cancelButton}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── AI Sentence Generator Modal ── */}
      {showAIBulkEditModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} style={{ maxWidth: '800px' }}>
            <h2>🤖 AI Sentence {aiModalMode === 'generate' ? 'Regenerator' : 'Generator'}</h2>

            {/* Display vocabulary words that will be used as focal points */}
            {aiModalMode === 'generate' && currentContext && currentContext.selectedVocabularyWords.size > 0 && (
              <div style={{
                backgroundColor: '#e3f2fd',
                border: '2px solid #2196f3',
                borderRadius: '6px',
                padding: '12px 15px',
                marginBottom: '15px'
              }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#1565c0', fontSize: '14px' }}>
                  📚 Vocabulary Focus
                </h4>
                <p style={{ margin: '0 0 8px 0', color: '#555', fontSize: '13px' }}>
                  The AI will incorporate these vocabulary words into the sentence:
                </p>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  {Array.from(currentContext.selectedVocabularyWords.values()).map(({ wordData }) => (
                    <span
                      key={wordData.Identifier}
                      style={{
                        backgroundColor: '#1976d2',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      {wordData.Targ_Word}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Warning if no vocabulary words in generate mode */}
            {aiModalMode === 'generate' && currentContext && currentContext.selectedVocabularyWords.size === 0 && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '2px solid #ff9800',
                borderRadius: '6px',
                padding: '12px 15px',
                marginBottom: '15px'
              }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#f57c00', fontSize: '14px' }}>
                  ⚠️ No Vocabulary Words Selected
                </h4>
                <p style={{ margin: '0', color: '#555', fontSize: '13px' }}>
                  No vocabulary words are selected for this sentence. The AI will generate a sentence without specific vocabulary focus.
                  Consider adding vocabulary words first for better learning exercises.
                </p>
              </div>
            )}

            <div className={styles.modalSection}>
              <p style={{ marginBottom: '15px', color: '#666' }}>
                {aiModalMode === 'generate'
                  ? 'Describe how you want to modify the generated sentences. The AI will create a new version based on your feedback.'
                  : 'Describe what kind of NEW sentence you want to generate. The AI will create a completely new sentence based on your instructions.'
                }
              </p>

              <div style={{ marginBottom: '20px' }}>
                <h4>Example Instructions:</h4>
                <ul style={{ color: '#888', fontSize: '14px', lineHeight: '1.6' }}>
                  <li>"A simple greeting using 'yes' for beginners"</li>
                  <li>"A sentence about ordering food at a restaurant"</li>
                  <li>"A past tense sentence about going to the store"</li>
                  <li>"A question asking about someone's day"</li>
                  <li>"A casual conversation starter for friends"</li>
                  <li>"A formal business introduction"</li>
                </ul>
              </div>

              <label className={styles.label}>
                Instructions for New Sentence:
                <textarea
                  value={aiBulkEditPrompt}
                  onChange={(e) => setAIBulkEditPrompt(e.target.value)}
                  placeholder="Describe the sentence you want to generate..."
                  className={styles.input}
                  rows={4}
                  disabled={isProcessingAIEdit}
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </label>
            </div>

            {/* AI Edit Preview */}
            {aiEditPreview && (() => {
              const aiData = (window as any).aiGeneratedTranslations;
              const currentBaseLanguage = editedExerciseData?.baseLanguage || 'EN';

              return (
                <div className={styles.modalSection} style={{
                  backgroundColor: '#f0f8ff',
                  padding: '15px',
                  borderRadius: '8px',
                  maxHeight: '500px',
                  overflowY: 'auto'
                }}>
                  <h3>Preview Generated Sentence:</h3>

                  <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e8f5e9', borderRadius: '6px' }}>
                    <strong style={{ fontSize: '16px' }}>Target Sentence ({editedExerciseData?.targetLanguage || 'ES'}):</strong>
                    <div style={{ marginTop: '8px', color: '#2a7f2a', fontWeight: 'bold', fontSize: '18px' }}>
                      {aiData?.sentenceTarget || aiEditPreview.sentenceTarget}
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <strong>Word Segments:</strong> {aiData?.wordsOrderedTarget || aiEditPreview._wordsOrderedTargetCsv}
                    </div>
                    <div style={{ marginTop: '4px' }}>
                      <strong>Distractors:</strong> {aiData?.distractorsTarget || aiEditPreview._distractorsTargetCsv || <em style={{ color: '#888' }}>None</em>}
                    </div>
                  </div>

                  <h4 style={{ marginBottom: '10px' }}>Base Language Translations ({aiData?.translations?.length || 0}):</h4>

                  {aiData?.translations?.map((translation: any) => {
                    const isCurrentLanguage = translation.baseLangCode === currentBaseLanguage;
                    return (
                      <div key={translation.baseLangCode} style={{
                        marginBottom: '12px',
                        padding: '12px',
                        backgroundColor: isCurrentLanguage ? '#e3f2fd' : '#fff',
                        border: isCurrentLanguage ? '3px solid #1976d2' : '2px solid #c8e6c9',
                        borderRadius: '6px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <div style={{ fontWeight: 'bold', color: isCurrentLanguage ? '#1565c0' : '#1976d2' }}>
                            {translation.baseLangCode} ({
                              translation.baseLangCode === 'EN' ? 'English' :
                              translation.baseLangCode === 'FR' ? 'French' :
                              translation.baseLangCode === 'DE' ? 'German' :
                              translation.baseLangCode === 'IT' ? 'Italian' :
                              translation.baseLangCode === 'PT' ? 'Portuguese' : translation.baseLangCode
                            })
                          </div>
                          {isCurrentLanguage && (
                            <span style={{
                              fontSize: '12px',
                              padding: '2px 8px',
                              backgroundColor: '#1976d2',
                              color: 'white',
                              borderRadius: '4px'
                            }}>
                              Currently Editing
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '16px', marginBottom: '6px' }}>
                          <strong>Sentence:</strong> {translation.sentenceSource}
                        </div>
                        <div style={{ fontSize: '14px', color: '#555', marginBottom: '4px' }}>
                          <strong>Word Segments:</strong> {translation.wordsOrderedBase}
                        </div>
                        <div style={{ fontSize: '14px', color: '#555' }}>
                          <strong>Distractors:</strong> {translation.distractorsBase || <em style={{ color: '#888' }}>None</em>}
                        </div>
                      </div>
                    );
                  })}

                  <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', fontSize: '14px' }}>
                    <strong>Options:</strong>
                    <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                      <li><strong>"Apply to Current Exercise"</strong> - Updates only the {currentBaseLanguage} variation you're editing</li>
                      <li><strong>"Update All Variations"</strong> - Updates all {aiData?.translations?.length || 0} base language variations with the new sentence</li>
                    </ul>
                  </div>
                </div>
              );
            })()}

            {/* Modal Actions */}
            <div className={styles.modalActions}>
              {!aiEditPreview ? (
                <>
                  <button
                    onClick={handleProcessAIEdit}
                    className={`${styles.button} ${styles.saveButton}`}
                    disabled={isProcessingAIEdit || !aiBulkEditPrompt.trim()}
                    style={{ backgroundColor: '#4a90e2' }}
                  >
                    {isProcessingAIEdit ? "Generating..." : "Generate New Sentence"}
                  </button>
                  <button
                    onClick={handleCloseAIBulkEdit}
                    className={styles.button}
                    disabled={isProcessingAIEdit}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleApplyAIEdit}
                    className={`${styles.button} ${styles.saveButton}`}
                    style={{ backgroundColor: '#2a7f2a' }}
                    disabled={isProcessingAIEdit}
                  >
                    {aiModalMode === 'generate' ? 'Apply to Generated Sentences' : 'Apply to Current Exercise'}
                  </button>
                  {aiModalMode === 'edit' && (
                    <button
                      onClick={handleUpdateAllVariations}
                      className={`${styles.button} ${styles.saveButton}`}
                      style={{ backgroundColor: '#1976d2' }}
                      disabled={isProcessingAIEdit}
                    >
                      {isProcessingAIEdit ? "Updating..." : "Update All Variations"}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setAIEditPreview(null);
                      setAIBulkEditPrompt("");
                    }}
                    className={styles.button}
                    disabled={isProcessingAIEdit}
                  >
                    {aiModalMode === 'generate' ? 'Regenerate Again' : 'Generate Different Sentence'}
                  </button>
                  <button
                    onClick={handleCloseAIBulkEdit}
                    className={styles.button}
                    disabled={isProcessingAIEdit}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── NEW: Summary of All Sentences Below ── */}
      {sentenceContexts.length > 0 && (
        <div
          style={{
            marginTop: "40px",
            padding: "20px",
            border: "1px solid #ddd",
            borderRadius: "4px",
            backgroundColor: "#fafafa",
          }}
        >
          <h3 className={styles.subHeading}>All Sentences Overview</h3>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
            {sentenceContexts.map((ctx) => (
              <li key={ctx.id} style={{ marginBottom: "8px" }}>
                <strong>{ctx.name}:</strong>{" "}
                {ctx.editableSourceBundleForm &&
                ctx.editableSourceBundleForm.variations.length > 0
                  ? ctx.editableSourceBundleForm.variations[0].text
                  : "(no text generated)"}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default VocabularySentenceGenerator;
